import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { es } from "date-fns/locale";
import { format, startOfMonth, endOfMonth, isSameMonth } from "date-fns";
import axios from "axios";
import { API } from "../App";
import {
  generateReservationsPDF,
  generateCurrentWeekPDF,
} from "../utils/pdfGenerator";
import { Download, FileText, Loader2, Eye } from "lucide-react";
import jsPDF from "jspdf";

// 🔌 Deriva la URL del WebSocket a partir de la URL del API (http->ws, https->wss),
// quitando el sufijo "/api" porque el WS vive en la raíz del backend (/ws/reservations).
const getWsUrl = () => {
  try {
    const httpBase = API.replace(/\/api\/?$/, '');
    return httpBase.replace(/^http/, 'ws') + '/ws/reservations';
  } catch {
    return null;
  }
};

// ===== Configuración de los campos de reserva según el nivel del docente =====
const GRADOS_INICIAL = ["3 Años", "4 Años", "5 Años"];
const GRADOS_PRIMARIA = ["1º", "2º", "3º", "4º", "5º", "6º"];
const GRADOS_SECUNDARIA = ["1º", "2º", "3º", "4º", "5º"];

const AREAS_INICIAL = [
  "Personal Social", "Psicomotriz", "Comunicación", "Matemática",
  "Ciencia y Tecnología", "Inglés", "Tutoría",
];
const AREAS_PRIMARIA = [
  "Matemática", "Comunicación", "Personal Social", "Ciencia y Tecnología",
  "Educación Física", "Arte y Cultura", "Educación Religiosa", "Inglés", "Tutoría",
];
const AREAS_SECUNDARIA = [
  "Matemática", "Comunicación", "Ciencias Sociales", "Ciencia y Tecnología",
  "Educación Física", "Arte y Cultura", "Educación Religiosa",
  "Educación para el Trabajo", "Desarrollo Personal", "Ciudadanía y Cívica", "Inglés", "Tutoría",
];

// Opciones de selección múltiple para "Recursos Materiales" en Detalles de la Reserva
const RECURSOS_MATERIALES_OPTIONS = [
  "Laptop", "Proyector", "Pizarra interactiva",
  "Puntero láser para presentaciones", "Parlantes", "Computadoras",
];

// Un docente puede trabajar en más de un nivel; se guarda como "Inicial,Primaria"
const parseNiveles = (nivelStr) =>
  (nivelStr || "").split(",").map((n) => n.trim()).filter(Boolean);

// Devuelve qué campos mostrar/pedir según el nivel del docente logueado.
// gradoOptions/areaOptions = null significa "mostrar como texto libre" (solo pasa en freeform).
const getNivelFormConfig = (nivelRaw) => {
  const nivel = (nivelRaw || "").trim().toLowerCase();

  if (nivel === "directivo" || nivel === "aip") {
    return { showGrado: false, showSeccion: false, showArea: false };
  }
  if (nivel === "inicial") {
    return {
      showGrado: true, gradoOptions: GRADOS_INICIAL,
      showSeccion: false,
      showArea: true, areaOptions: AREAS_INICIAL,
    };
  }
  if (nivel === "primaria") {
    return {
      showGrado: true, gradoOptions: GRADOS_PRIMARIA,
      showSeccion: true,
      showArea: true, areaOptions: AREAS_PRIMARIA,
    };
  }
  if (nivel === "secundaria") {
    return {
      showGrado: true, gradoOptions: GRADOS_SECUNDARIA,
      showSeccion: true,
      showArea: true, areaOptions: AREAS_SECUNDARIA,
    };
  }
  // Nivel no reconocido: modo seguro, todos los campos como texto libre
  return {
    showGrado: true, gradoOptions: null,
    showSeccion: true,
    showArea: true, areaOptions: null,
  };
};

const UserDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedShift, setSelectedShift] = useState('');
  // ✅ Ahora es un arreglo: 1 bloque normal, o 2 bloques continuos (sin recreo de por medio)
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [myReservations, setMyReservations] = useState([]);

  // 📅 Recordatorio: reservas del docente para HOY, ordenadas por hora, para
  // que no se le olvide que tiene algo programado apenas abre la app.
  // ⏱️ Se actualiza cada minuto para que el recordatorio de "hoy" desaparezca
  // solo apenas termine la reserva, sin necesidad de recargar la página.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const todaysReservations = useMemo(() => {
    const todayStr = format(nowTick, "yyyy-MM-dd");
    const nowMinutes = nowTick.getHours() * 60 + nowTick.getMinutes();

    return (myReservations || [])
      .filter((r) => {
        if (r.date !== todayStr) return false;
        // Se deja de mostrar apenas termina el bloque (no solo por fecha)
        const [, endStr] = (r.time_slot || "").split(" - ").map((s) => s.trim());
        if (!endStr) return true;
        const [endH, endM] = endStr.split(":").map(Number);
        const endMinutes = endH * 60 + endM;
        return nowMinutes < endMinutes;
      })
      .sort((a, b) => a.time_slot.localeCompare(b.time_slot));
  }, [myReservations, nowTick]);

  const [dayReservations, setDayReservations] = useState([]);
  const [showPastReservations, setShowPastReservations] = useState(false);
  const dateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;

const reservationsSameShift = (myReservations || []).filter(
  r => r && r.date === dateStr && r.shift === selectedShift
);

// Directivo y AIP no tienen límite de reservas por turno: coordinan el aula
// todo el día, no dictan una clase puntual como el resto de docentes.
const userNivelesForLimit = parseNiveles(userData?.nivel);
const esDirectivoOAipSinLimite = userNivelesForLimit.some((n) =>
  ["directivo", "aip"].includes(n.trim().toLowerCase())
);

// Reservar 2 bloques continuos consume el límite completo (2) de una sola vez
const wouldExceedLimit = !esDirectivoOAipSinLimite && reservationsSameShift.length + selectedTimeSlots.length > 2;

// Dos bloques son "continuos" si son vecinos en el orden oficial de bloques
// del turno (aunque tengan el recreo de por medio y las horas no calcen)
const areSlotsContiguous = (slotA, slotB) => {
  const lista = timeSlotsByShift[selectedShift] || [];
  const idxA = lista.indexOf(slotA);
  const idxB = lista.indexOf(slotB);
  if (idxA === -1 || idxB === -1) return false;
  return Math.abs(idxA - idxB) === 1;
};

// Número de hora (1H, 2H...) de un bloque ya reservado, según su turno.
// Se usa para mostrar "1H", "2H", etc. en el panel de "Mis reservas".
const getHourNumber = (shift, timeSlot) => {
  const lista = timeSlotsByShift[shift] || [];
  const idx = lista.indexOf(timeSlot);
  return idx === -1 ? null : idx + 1;
};

// Click en un bloque de horario:
// - Si ya estaba seleccionado -> se deselecciona.
// - Si no había ninguno seleccionado -> se selecciona solo.
// - Si ya había 1 seleccionado y el nuevo es continuo -> quedan los 2, ordenados.
// - Si ya había 1 seleccionado y el nuevo NO es continuo, o ya había 2 -> empieza de nuevo con el nuevo.
const toggleTimeSlotSelection = (slot) => {
  setSelectedTimeSlots((prev) => {
    if (prev.includes(slot)) {
      return prev.filter((s) => s !== slot);
    }
    if (prev.length === 1 && areSlotsContiguous(prev[0], slot)) {
      return [prev[0], slot].sort((a, b) => a.localeCompare(b));
    }
    return [slot];
  });
};
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  // 🗓️ Año seleccionado para el informe en PDF (evita mezclar, por ejemplo,
  // "Marzo" de dos años distintos en un mismo informe).
  const [selectedReportYear, setSelectedReportYear] = useState(new Date().getFullYear());
  // 🎓 Nivel elegido para el informe (solo se usa si el docente trabaja en más de un nivel).
  const [selectedReportLevel, setSelectedReportLevel] = useState("");
  const [isDownloadingWeek, setIsDownloadingWeek] = useState(false);
  const [isReservationDetailsOpen, setIsReservationDetailsOpen] = useState(false);
  // 🔁 Reserva recurrente: repetir el mismo horario cada semana hasta una fecha
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState("");
  const [isSubmittingRecurring, setIsSubmittingRecurring] = useState(false);
  const [reservationDetails, setReservationDetails] = useState({
    nivel: "",
    grado: "",
    seccion: "",
    area: "",
    actividad: "",
    recursos_digitales: "",
    recursos_materiales: [],
    competencia: "",
    enlaces: [],
  });

  // 📎 Archivos adjuntos opcionales (Word/Excel/PPT/PDF/HTML/JSON/audio/video).
  // Se pueden elegir varios. Se suben DESPUÉS de crear la reserva (necesita
  // el id que devuelve el backend), uno por uno.
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const ATTACHMENT_ACCEPT = ".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.html,.htm,.json,.jpg,.jpeg,.png,.gif,.webp,.mp3,.wav,.ogg,.m4a,.mp4,.mov,.avi,.webm,.mkv";
  const ATTACHMENT_MAX_MB = 50;
  const ATTACHMENT_MAX_COUNT = 5; // debe coincidir con MAX_ATTACHMENTS_PER_RESERVATION del backend

  const handleAttachmentChange = (e, existingCount = 0) => {
    const nuevos = Array.from(e.target.files || []);
    e.target.value = ""; // permite volver a elegir el mismo archivo si lo quita y lo agrega de nuevo
    if (nuevos.length === 0) return;

    const validos = [];
    for (const f of nuevos) {
      if (f.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
        toast.error(`"${f.name}" supera el límite de ${ATTACHMENT_MAX_MB} MB`);
        continue;
      }
      validos.push(f);
    }
    if (validos.length === 0) return;

    setAttachmentFiles((prev) => {
      const combinados = [...prev, ...validos];
      const cupoDisponible = ATTACHMENT_MAX_COUNT - existingCount;
      if (combinados.length > cupoDisponible) {
        toast.warning(`Solo puedes adjuntar hasta ${ATTACHMENT_MAX_COUNT} archivos por reserva. Se tomaron los primeros.`);
      }
      return combinados.slice(0, Math.max(cupoDisponible, 0));
    });
  };

  const removeAttachmentFile = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Sube los archivos elegidos (si hay) a la reserva indicada, uno por uno.
  // No aborta la reserva si alguna subida falla: la reserva ya quedó hecha,
  // solo se avisa cuáles archivos no se pudieron adjuntar.
  const uploadAttachmentIfAny = async (reservationId) => {
    if (attachmentFiles.length === 0 || !reservationId) return;
    setIsUploadingAttachment(true);
    let exitosos = 0;
    const fallidos = [];
    for (const f of attachmentFiles) {
      try {
        const formData = new FormData();
        formData.append("file", f);
        await axios.post(`${API}/reservations/${reservationId}/attachment`, formData, {
          headers: { ...axiosConfig.headers, "Content-Type": "multipart/form-data" },
        });
        exitosos++;
      } catch (error) {
        // Si el servidor web (proxy) corta la subida antes de que llegue a la
        // aplicación, no hay mensaje "detail": se explica la causa probable.
        let detail = error.response?.data?.detail;
        if (!detail) {
          if (error.response?.status === 413) {
            detail = "El servidor web rechazó el archivo por su tamaño (413). Hay que aumentar el límite de subida del servidor.";
          } else if (error.response) {
            detail = `El servidor respondió con error ${error.response.status}. Puede ser un límite de tamaño o tiempo del servidor web.`;
          } else {
            detail = "La subida se cortó (conexión o tiempo de espera del servidor). Prueba con un archivo más liviano o revisa el límite de subida del servidor.";
          }
        }
        fallidos.push({ name: f.name, detail });
      }
    }
    setIsUploadingAttachment(false);
    setAttachmentFiles([]);
    if (exitosos > 0) {
      toast.success(exitosos === 1 ? "Archivo adjuntado correctamente" : `${exitosos} archivos adjuntados correctamente`);
    }
    fallidos.forEach((f) => toast.error(`"${f.name}": ${f.detail}`));
  };

  // Elimina un archivo YA subido a una reserva (usado desde el modal de editar).
  const handleDeleteExistingAttachment = async (reservationId, attachmentId) => {
    try {
      await axios.delete(`${API}/reservations/${reservationId}/attachment/${attachmentId}`, axiosConfig);
      toast.success("Archivo eliminado");
      setEditingReservation((prev) =>
        prev
          ? { ...prev, attachments: (prev.attachments || []).filter((a) => a.id !== attachmentId) }
          : prev
      );
      await loadMyReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el archivo");
    }
  };

  // ✅ Cesión de reservas entre docentes
  const [outgoingTransfers, setOutgoingTransfers] = useState([]);
  const [incomingTransfers, setIncomingTransfers] = useState([]);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferReservation, setTransferReservation] = useState(null);
  const [transferToDni, setTransferToDni] = useState("");
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);
  const [transferToAccept, setTransferToAccept] = useState(null);
  const [acceptFormData, setAcceptFormData] = useState({ nivel: "", grado: "", seccion: "", area: "", actividad: "" });

  // ✅ Editar mi propia reserva (grado/sección/área/actividad, no el horario)
  const [editingReservation, setEditingReservation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    nivel: "", grado: "", seccion: "", area: "", actividad: "",
    recursos_digitales: "", recursos_materiales: [], competencia: "", enlaces: [],
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // ✅ Top 5 docentes activos de mi(s) propio(s) nivel(es)
  const [topTeachersByLevel, setTopTeachersByLevel] = useState({});
  const [topTeachersVisible, setTopTeachersVisible] = useState(true);
  const [attendanceMode, setAttendanceMode] = useState('manual');
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkinResultMsg, setCheckinResultMsg] = useState(null); // {ok: bool, text: string} | null
  const [lastCheckinCode, setLastCheckinCode] = useState(null); // código QR del intento más reciente, para "Reintentar"
  const [justCheckedInIds, setJustCheckedInIds] = useState([]); // ids de la(s) reserva(s) recién confirmadas por check-in (2 si eran 2 bloques)
  const [isSubmittingAccept, setIsSubmittingAccept] = useState(false);

  // 🔒 Estado global del sistema de reservas (lo controla el admin). Si está
  // desactivado, se bloquea la pantalla del docente al instante vía WebSocket.
  const [reservationsEnabled, setReservationsEnabled] = useState(true);
  const [reservationsMotivo, setReservationsMotivo] = useState("");

  const navigate = useNavigate();

    const token = localStorage.getItem("token");
  let storedUserData = {};

  try {
    storedUserData = JSON.parse(localStorage.getItem("userData")) || {};
  } catch (error) {
    console.error("Error leyendo userData del localStorage");
    localStorage.removeItem("userData");
}
  const userDni = storedUserData?.dni;

  const currentDate = new Date();
  const startOfCurrMonth = startOfMonth(currentDate);
  const endOfCurrMonth = endOfMonth(currentDate);

  // ✅ Los bloques de horario ya NO están escritos a mano aquí: se piden al
  // backend (única fuente de verdad, en server.py). Así, si el horario
  // cambia, basta con editarlo en un solo lugar.
  const [timeSlotsByShift, setTimeSlotsByShift] = useState({ mañana: [], tarde: [] });

  useEffect(() => {
    axios.get(`${API}/system/time-slots`)
      .then((res) => setTimeSlotsByShift(res.data))
      .catch((err) => console.error("Error cargando los horarios:", err));
  }, []);

  const axiosConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token],
  );

  // 🔒 Si el token guardado deja de ser válido (expiró o fue revocado) mientras
  // el docente sigue navegando —es decir, SIN haber cerrado sesión—, el
  // backend responde 401 "Token inválido" en cualquier petición. Antes esto
  // no se manejaba: la pantalla se quedaba "colgada" mostrando errores en
  // cada llamada. Ahora se detecta acá, se limpia la sesión guardada y se
  // regresa automáticamente a /user-login.
  useEffect(() => {
    // Se reinicia el "guard" cada vez que el panel se monta (por ejemplo,
    // tras un login exitoso), para no arrastrar el bloqueo de una sesión
    // anterior ya cerrada.
    sessionStorage.removeItem("session_expired_redirect");

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          const alreadyRedirecting = sessionStorage.getItem("session_expired_redirect");
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          if (!alreadyRedirecting) {
            sessionStorage.setItem("session_expired_redirect", "1");
            toast.error("Tu sesión expiró o el token ya no es válido. Vuelve a iniciar sesión.");
            navigate("/user-login");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [navigate]);

  // ===== 🔔 NOTIFICACIONES PUSH =====
  // Se muestran aunque el docente no tenga la app abierta (como WhatsApp).
  // "default" = todavía no se le preguntó, "granted" = ya aceptó,
  // "denied" = las rechazó (ya no se le puede volver a pedir por código).
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported =
    typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

  // Registra el Service Worker apenas carga la app (esto NO pide permiso,
  // solo deja el "cartero" listo para cuando el docente acepte notificaciones).
  useEffect(() => {
    if (pushSupported) {
      navigator.serviceWorker.register("/sw.js").catch((err) => {
        console.error("Error registrando el service worker:", err);
      });
    }
  }, [pushSupported]);

  // Convierte la llave pública VAPID (base64url) al formato que pide el navegador
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
  };

  const enablePushNotifications = async () => {
    if (!pushSupported) {
      toast.error("Tu navegador no soporta notificaciones push");
      return;
    }
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== "granted") {
        toast.warning("No se activaron las notificaciones (permiso no concedido)");
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      if (!data?.publicKey) {
        toast.error("Las notificaciones push no están configuradas en el servidor todavía");
        return;
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), axiosConfig);
      toast.success("🔔 Notificaciones activadas");
    } catch (error) {
      console.error("Error activando notificaciones push:", error);
      toast.error("No se pudieron activar las notificaciones");
    } finally {
      setPushLoading(false);
    }
  };

 const toDateLocal = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  const isReservationPast = (reservation) => {
    const now = new Date();
    const reservationDate = toDateLocal(reservation.date);
    if (!reservationDate) return false;
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    if (reservationDate < todayStart) return true;
    if (format(reservationDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd")) {
      const endTimeStr = reservation.time_slot.split(" - ")[1].trim();
      const [hours, minutes] = endTimeStr.split(":").map(Number);
      const resDateTime = new Date(reservationDate);
      resDateTime.setHours(hours, minutes, 0, 0);
      return resDateTime <= now;
    }
    return false;
  };

  const loadMyReservations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reservations/my`, axiosConfig);
      setMyReservations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos");
      setMyReservations([]);
    }
  }, [axiosConfig]);

  const loadOutgoingTransfers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transfers/outgoing`, axiosConfig);
      setOutgoingTransfers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setOutgoingTransfers([]);
    }
  }, [axiosConfig]);

  const loadIncomingTransfers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transfers/incoming`, axiosConfig);
      setIncomingTransfers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setIncomingTransfers([]);
    }
  }, [axiosConfig]);

  const loadTopTeachers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reservations/top-teachers`, axiosConfig);
      setTopTeachersByLevel(response.data || {});
    } catch (error) {
      setTopTeachersByLevel({});
    }
  }, [axiosConfig]);

  const loadTopTeachersVisibility = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/system/top-teachers-visibility`, axiosConfig);
      setTopTeachersVisible(response.data?.visible !== false);
    } catch (error) {
      setTopTeachersVisible(true);
    }
  }, [axiosConfig]);

  const loadAttendanceMode = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/system/attendance-mode`, axiosConfig);
      setAttendanceMode(response.data?.mode === 'qr' ? 'qr' : 'manual');
    } catch (error) {
      setAttendanceMode('manual');
    }
  }, [axiosConfig]);

  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const loadRemindersEnabled = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/system/reminders-status`, axiosConfig);
      setRemindersEnabled(response.data?.enabled !== false);
    } catch (error) {
      setRemindersEnabled(true);
    }
  }, [axiosConfig]);

  // Calendario de disponibilidad del día seleccionado (qué bloques ya están
  // ocupados). Se reutiliza tanto al cambiar de fecha como al recibir un
  // evento por WebSocket, para que se vea al instante si otro docente (o el
  // admin) reserva/cancela algo ese mismo día, sin tener que cambiar de fecha.
  const loadDayReservations = useCallback(async (date) => {
    if (!date) return;
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(
        `${API}/reservations/day?date=${dateStr}`,
        axiosConfig,
      );
      setDayReservations(response.data || []);
    } catch (error) {
      setDayReservations([]);
    }
  }, [axiosConfig]);

  // 🔒 Estado del sistema de reservas (público, no requiere token)
  const loadReservationsStatus = useCallback(async () => {
    try {
      const [statusRes, motivoRes] = await Promise.all([
        axios.get(`${API}/system/reservations-status`),
        axios.get(`${API}/system/reservations-motivo`),
      ]);
      setReservationsEnabled(statusRes.data?.enabled !== false);
      setReservationsMotivo(motivoRes.data?.motivo || "");
    } catch (error) {
      setReservationsEnabled(true);
      setReservationsMotivo("");
    }
  }, []);

  // ✅ Check-in por QR: al escanear el código de la sala, la app abre esta misma
  // pantalla con "?checkin=1" en la URL. Si el docente ya está logueado, se
  // marca su asistencia automáticamente sin que tenga que hacer nada más.
  const handleCheckin = useCallback(async (qrCode) => {
    setIsCheckingIn(true);
    setCheckinResultMsg(null);
    setLastCheckinCode(qrCode || null);
    try {
      const res = await axios.post(`${API}/checkin`, { code: qrCode || null }, axiosConfig);
      setCheckinResultMsg({ ok: true, text: res.data?.message || "¡Check-in exitoso!" });
      setJustCheckedInIds(res.data?.ids_marcados?.length ? res.data.ids_marcados : (res.data?.id ? [res.data.id] : []));
      toast.success(res.data?.message || "¡Check-in exitoso!");
      await loadMyReservations();
    } catch (error) {
      const msg = error.response?.data?.detail || "No se pudo hacer el check-in";
      setCheckinResultMsg({ ok: false, text: msg });
      toast.error(msg);
    } finally {
      setIsCheckingIn(false);
      // Limpia el "?checkin=1" de la URL para que un refresh no lo repita
      const url = new URL(window.location.href);
      url.searchParams.delete("checkin");
      url.searchParams.delete("code");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [axiosConfig, loadMyReservations]);

  // 🔌 Sincronización en tiempo real: escucha los eventos que emite el
  // backend por WebSocket (reservas, cesiones, configuración) y refresca
  // solo lo que cambió, sin recargar la página ni cerrar/abrir sesión.
  // Si el administrador desactiva a este docente, se cierra su sesión al instante.
  const wsRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);
  useEffect(() => {
    if (!token || !userDni) return undefined;

    let reconnectTimer = null;
    let closedByUs = false;

    const connect = () => {
      const wsUrl = getWsUrl();
      console.log('[WS docente] intentando conectar a:', wsUrl, '(API =', API, ')');
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS docente] conectado a', wsUrl);
      };

      ws.onmessage = (evt) => {
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return;
        }
        console.log('[WS docente] evento recibido:', msg);
        switch (msg.event) {
          case 'reservation_created':
          case 'reservation_deleted':
          case 'reservation_updated':
          case 'reservation_attendance_updated':
          case 'reservations_changed':
            loadMyReservations();
            loadTopTeachers();
            loadDayReservations(selectedDateRef.current);
            break;
          case 'config_updated':
            loadTopTeachersVisibility();
            loadAttendanceMode();
            loadRemindersEnabled();
            // El propio mensaje ya trae "enabled" y "motivo": actualizamos
            // de inmediato sin esperar otra petición, para bloquear/desbloquear
            // la pantalla del docente al instante.
            if (typeof msg.enabled === 'boolean') {
              setReservationsEnabled(msg.enabled);
            }
            if (typeof msg.motivo === 'string') {
              setReservationsMotivo(msg.motivo);
            }
            break;
          case 'user_status_updated':
            if (msg.dni === userDni && msg.active === false) {
              toast.error('Tu cuenta fue desactivada por un administrador');
              localStorage.removeItem('token');
              localStorage.removeItem('userData');
              navigate('/user-login');
            }
            break;
          default:
            break;
        }
      };

      ws.onclose = (evt) => {
        console.log('[WS docente] desconectado. code:', evt.code, 'reason:', evt.reason);
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS docente] error de conexión:', err);
        ws.close();
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [token, userDni, loadMyReservations, loadTopTeachers, loadTopTeachersVisibility, loadAttendanceMode, loadRemindersEnabled, loadDayReservations, navigate]);

  useEffect(() => {
  let data = {};

  try {
    data = JSON.parse(localStorage.getItem("userData")) || {};
  } catch (error) {
    console.error("Error leyendo userData");
    localStorage.removeItem("userData");
  }

  setUserData(data);

  // ✅ Si venimos de escanear el QR, guardamos esa intención ANTES de la
  // posible redirección al login, para que no se pierda. Así, sin importar
  // si el docente ya estaba logueado o tiene que iniciar sesión primero,
  // el check-in se dispara apenas vuelva a esta pantalla.
  const params = new URLSearchParams(window.location.search);
  if (params.get("checkin") === "1") {
    sessionStorage.setItem("pending_checkin", "1");
    // El código secreto viene en la URL del QR real (ej. ?checkin=1&code=XYZ).
    // Sin este código, el backend rechaza el check-in — así se evita que
    // alguien registre asistencia sin haber escaneado el QR físico del AIP.
    const qrCode = params.get("code");
    if (qrCode) {
      sessionStorage.setItem("pending_checkin_code", qrCode);
    }
  }

  if (!token || !userDni) {
    navigate("/user-login");
    return;
  }

  loadMyReservations();
  loadOutgoingTransfers();
  loadIncomingTransfers();
  loadTopTeachers();
  loadTopTeachersVisibility();
  loadAttendanceMode();
  loadRemindersEnabled();
  loadReservationsStatus();

  if (sessionStorage.getItem("pending_checkin") === "1") {
    sessionStorage.removeItem("pending_checkin");
    const qrCode = sessionStorage.getItem("pending_checkin_code");
    sessionStorage.removeItem("pending_checkin_code");
    handleCheckin(qrCode);
  }
  }, [token, userDni, loadMyReservations, loadOutgoingTransfers, loadIncomingTransfers, loadTopTeachers, loadTopTeachersVisibility, loadAttendanceMode, loadRemindersEnabled, loadReservationsStatus, handleCheckin, navigate]);

  // ✅ Revisa periódicamente si llegaron nuevas cesiones, si alguna fue resuelta,
  // o si el Top 5 cambió (por ejemplo, porque otro docente hizo una reserva)
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      loadOutgoingTransfers();
      loadIncomingTransfers();
      loadTopTeachers();
    }, 8000);
    return () => clearInterval(interval);
  }, [token, loadOutgoingTransfers, loadIncomingTransfers, loadTopTeachers]);


  useEffect(() => {
    loadDayReservations(selectedDate);
  }, [selectedDate, loadDayReservations]);

  // Lógica para manejar la selección de fecha en el calendario, con validaciones específicas para domingos, fechas pasadas y meses disponibles
 const handleDateSelect = (date) => {
  if (!date) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ❌ Domingos
  if (date.getDay() === 0) {
    toast.warning("La institución está cerrada los domingos");
    return;
  }

  // ❌ Fechas pasadas
  if (date < today) {
    toast.warning("No puedes reservar en fechas pasadas");
    return;
  }

  const isCurrentMonth = isSameMonth(date, today);

  const nextMonthDate = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    1
  );

  const isNextMonth = isSameMonth(date, nextMonthDate);

  // 🔥 Calcular si faltan 2 días para terminar el mes
  const lastDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  );

  const diffInDays = Math.ceil(
    (lastDayOfMonth - today) / (1000 * 60 * 60 * 24)
  );

  const isNextMonthEnabled = diffInDays <= 2;

  // ❌ Bloquear si no es mes actual ni próximo habilitado
  if (!isCurrentMonth && !(isNextMonth && isNextMonthEnabled)) {
    toast.warning(
      "Solo puedes reservar en el mes actual o el próximo (si faltan 2 días para el cierre)"
    );
    return;
  }

  setSelectedDate(date);
  setSelectedShift("");
  setSelectedTimeSlots([]);
  setReservationDetails({ nivel: "", grado: "", seccion: "", area: "", actividad: "", recursos_digitales: "", recursos_materiales: [], competencia: "", enlaces: [] });
};

  const handleReserve = async () => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    // 1️⃣ Verificar si ya reservó alguno de los bloques seleccionados
  const alreadyReservedSlot = myReservations.find(
    r => r.date === dateStr && selectedTimeSlots.includes(r.time_slot)
  );

  if (alreadyReservedSlot) {
    toast.error("Ya reservaste ese horario");
    return;
  }

   // Contar reservas del mismo día Y del mismo turno (2 bloques continuos consumen el límite completo)
const reservationsSameShift = (myReservations || []).filter(
  r => r && r.date === dateStr && r.shift === selectedShift
);

if (!esDirectivoOAipSinLimite && reservationsSameShift.length + selectedTimeSlots.length > 2) {
  toast.error(`Límite: 2 reservas en el turno ${selectedShift}`);
  return;
}

    try {
      const res = await axios.post(
        `${API}/reservations`,
        {
          date: dateStr,
          shift: selectedShift,
          time_slots: selectedTimeSlots,
          user_dni: userDni,
          nivel: reservationDetails.nivel,
          grado: reservationDetails.grado,
          seccion: reservationDetails.seccion,
          area: reservationDetails.area,
          actividad: reservationDetails.actividad,
          recursos_digitales: reservationDetails.recursos_digitales,
          recursos_materiales: reservationDetails.recursos_materiales,
          competencia: reservationDetails.competencia,
          enlaces: reservationDetails.enlaces.filter((e) => e && e.trim()),
        },
        axiosConfig
      );

      const primeraReservaId = res.data?.reservations?.[0]?.id;
      await uploadAttachmentIfAny(primeraReservaId);

      toast.success(selectedTimeSlots.length === 2 ? "Reserva confirmada (2 bloques)" : "Reserva confirmada");
      await loadMyReservations();
      loadTopTeachers();

      setSelectedShift("");
      setSelectedTimeSlots([]);
      setReservationDetails({ nivel: "", grado: "", seccion: "", area: "", actividad: "", recursos_digitales: "", recursos_materiales: [], competencia: "", enlaces: [] });
      setIsReservationDetailsOpen(false);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reservar");
    }
  }; 

  // 🔁 Crea la reserva de hoy + una por semana (mismo día/horario) hasta
  // "repeatUntil" inclusive. El backend se encarga de omitir automáticamente
  // las fechas donde el horario ya esté ocupado, sin abortar el resto.
  const handleReserveRecurring = async () => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    const alreadyReservedSlot = myReservations.find(
      r => r.date === dateStr && selectedTimeSlots.includes(r.time_slot)
    );
    if (alreadyReservedSlot) {
      toast.error("Ya reservaste ese horario");
      return;
    }
    if (!repeatUntil) {
      toast.warning("Selecciona hasta qué fecha quieres repetir la reserva");
      return;
    }

    setIsSubmittingRecurring(true);
    try {
      const res = await axios.post(
        `${API}/reservations/recurring`,
        {
          date: dateStr,
          shift: selectedShift,
          time_slots: selectedTimeSlots,
          user_dni: userDni,
          nivel: reservationDetails.nivel,
          grado: reservationDetails.grado,
          seccion: reservationDetails.seccion,
          area: reservationDetails.area,
          actividad: reservationDetails.actividad,
          recursos_digitales: reservationDetails.recursos_digitales,
          recursos_materiales: reservationDetails.recursos_materiales,
          competencia: reservationDetails.competencia,
          enlaces: reservationDetails.enlaces.filter((e) => e && e.trim()),
          repeat_until: repeatUntil,
        },
        axiosConfig
      );

      const { creadas = [], omitidas = [], first_reservation_id } = res.data || {};

      await uploadAttachmentIfAny(first_reservation_id);

      if (omitidas.length > 0) {
        toast.warning(
          <div>
            <p className="font-black text-base mb-2 text-gray-900">
              {creadas.length > 0
                ? `${creadas.length} reserva(s) creada(s). ${omitidas.length} no se pudo(pudieron) hacer:`
                : "No se pudo crear ninguna reserva:"}
            </p>
            <ul className="text-sm sm:text-base font-semibold list-disc pl-4 space-y-1 text-gray-900">
              {omitidas.map((o, idx) => (
                <li key={idx}>
                  {format(new Date(o.date + "T00:00:00"), "EEEE dd 'de' MMMM", { locale: es })}: {o.motivo}
                </li>
              ))}
            </ul>
          </div>,
          {
            duration: 30000,
            closeButton: true,
            classNames: {
              toast: "!bg-amber-50 !border-2 !border-amber-400",
              closeButton:
                "!w-7 !h-7 !left-auto !right-2 !top-2 !bg-gray-900 !text-white !border-2 !border-white !opacity-100 !scale-110",
            },
          }
        );
      } else {
        toast.success(`${creadas.length} reserva(s) creada(s)`);
      }

      await loadMyReservations();
      loadTopTeachers();

      setSelectedShift("");
      setSelectedTimeSlots([]);
      setReservationDetails({ nivel: "", grado: "", seccion: "", area: "", actividad: "", recursos_digitales: "", recursos_materiales: [], competencia: "", enlaces: [] });
      setRepeatWeekly(false);
      setRepeatUntil("");
      setIsReservationDetailsOpen(false);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear las reservas recurrentes");
    } finally {
      setIsSubmittingRecurring(false);
    }
  };

  const handleOpenReservationDetails = () => {
    if (!selectedDate || !selectedShift || selectedTimeSlots.length === 0) return;
    const userNiveles = parseNiveles(userData?.nivel);
    setReservationDetails((prev) => ({
      ...prev,
      nivel: userNiveles.length === 1 ? userNiveles[0] : "",
    }));
    setRepeatWeekly(false);
    setRepeatUntil("");
    setIsReservationDetailsOpen(true);
  };

  const handleConfirmReservationDetails = () => {
    const { nivel, grado, seccion, area, actividad } = reservationDetails;
    const userNiveles = parseNiveles(userData?.nivel);

    if (userNiveles.length > 1 && !nivel) {
      toast.warning("Selecciona para qué nivel es esta reserva");
      return;
    }

    const config = getNivelFormConfig(nivel || userData?.nivel);

    if (!actividad.trim()) {
      toast.warning("Ingresa la actividad");
      return;
    }
    if (config.showGrado && !grado.trim()) {
      toast.warning("Selecciona el grado");
      return;
    }
    if (config.showSeccion && !seccion.trim()) {
      toast.warning("Ingresa la sección");
      return;
    }
    if (config.showArea && !area.trim()) {
      toast.warning("Selecciona el área");
      return;
    }
    if (repeatWeekly) {
      handleReserveRecurring();
    } else {
      handleReserve();
    }
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm("¿Eliminar reserva?")) return;
    try {
      await axios.delete(`${API}/reservations/${id}`, axiosConfig);
      setMyReservations((prev) => prev.filter((r) => r.id !== id));
      toast.success("Eliminada");
      loadTopTeachers();
    } catch (error) {
      toast.error("Error");
    }
  };

  // ✅ Docente A: iniciar cesión de una reserva
  const handleOpenTransferModal = (reservation) => {
    setTransferReservation(reservation);
    setTransferToDni("");
    setIsTransferModalOpen(true);
  };

  const handleSubmitTransfer = async () => {
    if (!transferReservation) return;
    if (!transferToDni.trim()) {
      toast.warning("Ingresa el DNI del docente");
      return;
    }
    setIsSubmittingTransfer(true);
    try {
      await axios.post(
        `${API}/reservations/${transferReservation.id}/transfer`,
        { to_dni: transferToDni.trim() },
        axiosConfig
      );
      toast.success("Reserva en proceso: esperando que el docente acepte.");
      setIsTransferModalOpen(false);
      setTransferReservation(null);
      setTransferToDni("");
      await loadOutgoingTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo iniciar la cesión");
    } finally {
      setIsSubmittingTransfer(false);
    }
  };

  const handleCancelTransfer = async (transferId) => {
    if (!window.confirm("¿Cancelar esta cesión en proceso?")) return;
    try {
      await axios.post(`${API}/transfers/${transferId}/cancel`, {}, axiosConfig);
      toast.success("Cesión cancelada");
      await loadOutgoingTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cancelar la cesión");
    }
  };

  // ✅ Docente B: aceptar o rechazar una cesión recibida
  const handleOpenAcceptModal = (transfer) => {
    const userNiveles = parseNiveles(userData?.nivel);
    setTransferToAccept(transfer);
    setAcceptFormData({
      nivel: userNiveles.length === 1 ? userNiveles[0] : "",
      grado: "",
      seccion: "",
      area: "",
      actividad: "",
    });
  };

  const handleConfirmAccept = async () => {
    if (!transferToAccept) return;
    const { nivel, grado, seccion, area, actividad } = acceptFormData;
    const userNiveles = parseNiveles(userData?.nivel);

    if (userNiveles.length > 1 && !nivel) {
      toast.warning("Selecciona para qué nivel es esta reserva");
      return;
    }

    const config = getNivelFormConfig(nivel || userData?.nivel);

    if (!actividad.trim()) {
      toast.warning("Ingresa la actividad");
      return;
    }
    if (config.showGrado && !grado.trim()) {
      toast.warning("Selecciona el grado");
      return;
    }
    if (config.showSeccion && !seccion.trim()) {
      toast.warning("Ingresa la sección");
      return;
    }
    if (config.showArea && !area.trim()) {
      toast.warning("Selecciona el área");
      return;
    }

    setIsSubmittingAccept(true);
    try {
      await axios.post(
        `${API}/transfers/${transferToAccept.id}/accept`,
        {
          nivel: nivel.trim(),
          grado: grado.trim(),
          seccion: seccion.trim(),
          area: area.trim(),
          actividad: actividad.trim(),
        },
        axiosConfig
      );
      toast.success("¡Reserva aceptada! Ahora es tuya.");
      setTransferToAccept(null);
      setAcceptFormData({ nivel: "", grado: "", seccion: "", area: "", actividad: "" });
      await Promise.all([loadIncomingTransfers(), loadMyReservations()]);
      loadTopTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo aceptar la reserva");
    } finally {
      setIsSubmittingAccept(false);
    }
  };

  const handleRejectTransfer = async (transferId) => {
    if (!window.confirm("¿Rechazar esta reserva? Se mantendrá con el docente original.")) return;
    try {
      await axios.post(`${API}/transfers/${transferId}/reject`, {}, axiosConfig);
      toast.success("Reserva rechazada");
      await loadIncomingTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo rechazar la reserva");
    }
  };

  // ✅ Editar mi propia reserva: grado/sección/área/actividad (y nivel, si aplica).
  // El horario (fecha, turno, hora) nunca se modifica desde aquí.
  const handleOpenEditModal = (reservation) => {
    const userNiveles = parseNiveles(userData?.nivel);
    const nivelActual = reservation.nivel || (userNiveles.length === 1 ? userNiveles[0] : "");
    setEditingReservation(reservation);
    setEditFormData({
      nivel: nivelActual,
      grado: reservation.grado || "",
      seccion: reservation.seccion || "",
      area: reservation.area || "",
      actividad: reservation.actividad || "",
      recursos_digitales: reservation.recursos_digitales || "",
      recursos_materiales: reservation.recursos_materiales || [],
      competencia: reservation.competencia || "",
      enlaces: reservation.enlaces && reservation.enlaces.length > 0 ? reservation.enlaces : [],
    });
    // 📎 Se limpia cualquier archivo que hubiera quedado seleccionado en el
    // modal de "Nueva reserva"; los archivos ya subidos (si existen) se
    // muestran a partir de reservation.attachments.
    setAttachmentFiles([]);
  };

  const handleConfirmEdit = async () => {
    if (!editingReservation) return;
    const { nivel, grado, seccion, area, actividad } = editFormData;
    const userNiveles = parseNiveles(userData?.nivel);

    if (userNiveles.length > 1 && !nivel) {
      toast.warning("Selecciona para qué nivel es esta reserva");
      return;
    }

    const config = getNivelFormConfig(nivel || userData?.nivel);

    if (!actividad.trim()) {
      toast.warning("Ingresa la actividad");
      return;
    }
    if (config.showGrado && !grado.trim()) {
      toast.warning("Selecciona el grado");
      return;
    }
    if (config.showSeccion && !seccion.trim()) {
      toast.warning("Ingresa la sección");
      return;
    }
    if (config.showArea && !area.trim()) {
      toast.warning("Selecciona el área");
      return;
    }

    setIsSubmittingEdit(true);
    try {
      await axios.put(
        `${API}/reservations/${editingReservation.id}`,
        {
          nivel: nivel.trim(),
          grado: grado.trim(),
          seccion: seccion.trim(),
          area: area.trim(),
          actividad: actividad.trim(),
          recursos_digitales: editFormData.recursos_digitales,
          recursos_materiales: editFormData.recursos_materiales,
          competencia: editFormData.competencia,
          enlaces: editFormData.enlaces.filter((e) => e && e.trim()),
        },
        axiosConfig
      );
      // 📎 Si el docente eligió archivos nuevos al editar, se suben ahora
      // (se agregan a los que ya tenía la reserva; no los reemplazan).
      await uploadAttachmentIfAny(editingReservation.id);
      toast.success("Reserva actualizada correctamente");
      setEditingReservation(null);
      setEditFormData({ nivel: "", grado: "", seccion: "", area: "", actividad: "" });
      await loadMyReservations();
      loadTopTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar la reserva");
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const getSlotStatus = (slot) => {
    const dateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
    const now = new Date();

    // 1. Verificar si ya es una reserva propia
    const myRes = myReservations.find(
      (r) => r.date === dateStr && r.time_slot === slot,
    );
    if (myRes) return { disabled: true, text: "Ya reservado por ti" };

    // 2. Verificar si está reservado por otro
    const otherRes = dayReservations.find((r) => r.time_slot === slot);
    if (otherRes)
      return {
        disabled: true,
        text: `Reservado por ${otherRes.user_name || "docente"}`,
      };

    // 3. BLOQUEO DE HORA: No permitir si ya pasó la hora de inicio (o falta menos de 1 hora)
    // if (
    //   selectedDate &&
    //   format(selectedDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd")
    // ) {
    //   const startTimeStr = slot.split(" - ")[0].trim(); // "17:00"
    //   const [hours, minutes] = startTimeStr.split(":").map(Number);

    //   const slotTime = new Date(selectedDate);
    //   slotTime.setHours(hours, minutes, 0, 0);

    //   // Restar 1 hora al horario del slot
    //   const limitTime = new Date(slotTime);
    //   limitTime.setHours(limitTime.getHours() - 1);

    //   // Si ya estamos dentro de la última hora antes del turno → bloquear
    //   if (now >= limitTime) {
    //     return { disabled: true, text: "Bloquedo" };
    //   }
    // }
    //return { disabled: false, text: "" };

      // 3. BLOQUEO DE HORA: No permitir si es el mismo día de hoy
    if (
      format(selectedDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd")
    ) {
      return { disabled: true, text: "Reserva con un día de anticipación" };
    }

    // 4. Slot libre y disponible
    return { disabled: false, text: "" };
  };

  // 📅 Antes solo mostraba reservas del mes actual — un docente que ya
  // reservó para el próximo mes (cuando se habilita cerca de fin de mes) no
  // las veía hasta que el calendario "rodara" a ese mes. Ahora se incluyen
  // también las reservas ya hechas para el mes siguiente.
  const endOfNextMonth = endOfMonth(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const reservationsThisMonth = (myReservations || []).filter((r) => {
    if (!r || !r.date) return false;

    const rDate = toDateLocal(r.date);
    if (!rDate) return false;

    return rDate >= startOfCurrMonth && rDate <= endOfNextMonth;
  });

  const currentAndFuture = reservationsThisMonth.filter(
    (r) => !isReservationPast(r),
  );
  const past = reservationsThisMonth.filter((r) => isReservationPast(r));

  // 🗓️ Años que tienen al menos una reserva propia, de más reciente a más
  // antiguo, para el selector de año del modal de descarga.
  const availableReportYears = useMemo(() => {
    const years = new Set(
      (myReservations || [])
        .filter(r => r && r.date)
        .map(r => new Date(r.date).getFullYear())
    );
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [myReservations]);

  // 🎓 Niveles del docente. Si trabaja en más de uno (ej. Inicial y Primaria),
  // el informe se descarga por nivel; si tiene uno solo, todo funciona como antes.
  const reportLevels = parseNiveles(userData?.nivel);
  const needsLevelPick = reportLevels.length > 1;
  const matchesReportLevel = (r, lvl) =>
    !needsLevelPick ||
    (r?.nivel || "").trim().toLowerCase() === (lvl || "").trim().toLowerCase();

  // Meses (1-12) que sí tienen reservas propias dentro del año seleccionado.
  const monthsWithData = useMemo(() => {
    return Array.from(
      new Set(
        (myReservations || [])
          .filter(r => matchesReportLevel(r, selectedReportLevel))
          .filter(r => r && r.date && new Date(r.date).getFullYear() === Number(selectedReportYear))
          .map(r => new Date(r.date).getMonth() + 1)
      )
    ).sort((a, b) => a - b);
  }, [myReservations, selectedReportYear, selectedReportLevel, userData]);

  // Cambiar de año dentro del modal: se reinicia la selección de meses.
  const handleSelectReportYear = (year) => {
    setSelectedReportYear(Number(year));
    setSelectedMonths(new Set());
  };

  // Cambiar de nivel dentro del modal: se preseleccionan los meses con reservas de ESE nivel.
  const handleSelectReportLevel = (level) => {
    setSelectedReportLevel(level);
    setSelectedMonths(
      new Set(
        (myReservations || [])
          .filter((r) => matchesReportLevel(r, level))
          .filter((r) => r && r.date && new Date(r.date).getFullYear() === Number(selectedReportYear))
          .map((r) => new Date(r.date).getMonth() + 1)
      )
    );
  };

  // Abre el modal de descarga ya posicionado en el año más reciente que
  // tenga reservas propias, con esos meses pre-seleccionados.
  const openDownloadModal = () => {
    const defaultLevel = reportLevels[0] || "";
    const yearWithData = availableReportYears.find((y) =>
      (myReservations || []).some(
        (r) => r?.date && new Date(r.date).getFullYear() === y && matchesReportLevel(r, defaultLevel)
      )
    ) ?? availableReportYears[0];
    setSelectedReportLevel(defaultLevel);
    setSelectedReportYear(yearWithData);
    setSelectedMonths(
      new Set(
        (myReservations || [])
          .filter((r) => matchesReportLevel(r, defaultLevel))
          .filter((r) => r && r.date && new Date(r.date).getFullYear() === yearWithData)
          .map((r) => new Date(r.date).getMonth() + 1)
      )
    );
    setIsDownloadModalOpen(true);
  };

  const getMonthName = (m) =>
    [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ][m - 1];

  // Calculamos si faltan 2 días o menos para el fin de mes
  const daysUntilMonthEnd = endOfCurrMonth.getDate() - currentDate.getDate();
  const isNextMonthEnabled = daysUntilMonthEnd <= 2;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#0f2419] p-6">
      {/* 🔒 Bloqueo total de la pantalla: se activa/desactiva al instante vía
          WebSocket cuando el administrador desactiva el sistema de reservas.
          El docente NO puede interactuar con nada mientras esté activo. */}
      {!reservationsEnabled && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-[2rem] shadow-2xl max-w-lg w-full p-10 text-center border-4 border-red-500">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-black text-red-700 mb-3">
              Sistema de Reservas Desactivado
            </h2>
            <p className="text-gray-700 font-medium mb-6">
              {reservationsMotivo?.trim()
                ? reservationsMotivo
                : "El administrador desactivó temporalmente el sistema de reservas. Vuelve a intentarlo más tarde."}
            </p>
            <Button
              onClick={() => {
                localStorage.clear();
                navigate("/");
              }}
              variant="outline"
              className="bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white font-bold"
            >
              Cerrar Sesión
            </Button>
          </div>
        </div>
      )}

      <div className="bg-[#0f2419]/60 backdrop-blur-sm border-b border-[#f5e6d3]/20 p-4 mb-8 rounded-2xl shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="/logo_aip.png"
              alt="Logo"
              className="w-14 h-14 object-contain rounded-full shadow-inner bg-white/10 p-1"
            />
            <div>
              <h1 className="text-2xl font-black text-[#f5e6d3] tracking-tight">
                Sistema de Reservas AIP
              </h1>
              <p className="text-[#f5e6d3]/80 text-sm font-medium">
                Docente: {userData?.nombres} {userData?.apellidos}
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white font-bold transition-all shadow-sm"
          >
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* 📅 Recordatorio: reservas de HOY, bien visible apenas se abre la app
          (el admin puede apagar esto desde Configuración > Recordatorios) */}
      {remindersEnabled && todaysReservations.length > 0 && (
        <div className="max-w-7xl mx-auto mb-6">
          <style>{`
            @keyframes ring-bell {
              0%, 100% { transform: rotate(0deg); }
              8% { transform: rotate(18deg); }
              16% { transform: rotate(-16deg); }
              24% { transform: rotate(14deg); }
              32% { transform: rotate(-12deg); }
              40% { transform: rotate(9deg); }
              48% { transform: rotate(-6deg); }
              56% { transform: rotate(3deg); }
              64%, 100% { transform: rotate(0deg); }
            }
            .reminder-ring {
              display: inline-block;
              transform-origin: 50% 15%;
              animation: ring-bell 2.4s ease-in-out infinite;
            }
          `}</style>
          <div className="rounded-2xl p-5 shadow-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-4">
            <span className="text-3xl leading-none reminder-ring">⏰</span>
            <div>
              <p className="font-black text-amber-900">
                {todaysReservations.length === 1
                  ? "Recuerda: tienes una reserva hoy"
                  : `Recuerda: tienes ${todaysReservations.length} reservas hoy`}
              </p>
              <p className="text-amber-800 text-sm font-semibold mt-1">
                {todaysReservations.map((r, i) => (
                  <span key={r.id}>
                    {getHourNumber(r.shift, r.time_slot) && `${getHourNumber(r.shift, r.time_slot)}H · `}
                    {r.time_slot} (Turno {r.shift})
                    {i < todaysReservations.length - 1 ? " · " : ""}
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 🔔 Ofrecer activar notificaciones push (solo si el navegador las soporta y aún no se le preguntó) */}
      {pushSupported && pushPermission === "default" && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="rounded-2xl p-5 shadow-lg border-2 border-[#2d6a4f] bg-[#f0f7f3] flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-3xl leading-none">🔔</span>
              <div>
                <p className="font-black text-[#03361c]">Activa las notificaciones</p>
                <p className="text-sm text-gray-600 font-semibold">
                  Te avisamos 1 hora antes de cada reserva, aunque no tengas la app abierta.
                </p>
              </div>
            </div>
            <Button
              onClick={enablePushNotifications}
              disabled={pushLoading}
              className="bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black px-6 py-5 rounded-xl shadow-lg"
            >
              {pushLoading ? "Activando..." : "Activar"}
            </Button>
          </div>
        </div>
      )}

      {/* ✅ Resultado del check-in por QR (aparece justo después de escanear) */}
      {(isCheckingIn || checkinResultMsg) && (
        <div className="max-w-7xl mx-auto mb-6">
          <div
            className={`rounded-2xl p-5 shadow-lg border-2 flex items-center gap-4 flex-wrap justify-between ${
              isCheckingIn
                ? "bg-blue-50 border-blue-200"
                : checkinResultMsg?.ok
                ? "bg-green-50 border-green-300"
                : "bg-red-50 border-red-300"
            }`}
          >
            <div className="flex items-center gap-4">
              <span className="text-3xl">
                {isCheckingIn ? "⏳" : checkinResultMsg?.ok ? "✅" : "⚠️"}
              </span>
              <p className={`font-bold ${isCheckingIn ? "text-blue-800" : checkinResultMsg?.ok ? "text-green-800" : "text-red-800"}`}>
                {isCheckingIn ? "Registrando tu check-in..." : checkinResultMsg?.text}
              </p>
            </div>
            {/* Solo reintenta un check-in que YA se disparó al escanear el QR real
                y falló por algo técnico (ej. corte de red) — no es un botón para
                auto-declararse presente sin haber escaneado nada. */}
            {!isCheckingIn && checkinResultMsg && checkinResultMsg.ok === false && (
              <Button
                onClick={() => handleCheckin(lastCheckinCode)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2 rounded-xl shrink-0"
              >
                🔄 Reintentar
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ✅ Recordatorio informativo de que el check-in es por QR (sin botón:
          la asistencia solo se registra escaneando el código físico del AIP,
          para evitar que el docente marque asistencia desde su casa). */}
      {attendanceMode === "qr" && !isCheckingIn && !checkinResultMsg && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="rounded-2xl p-4 shadow-md border-2 border-blue-200 bg-blue-50/70 flex items-center gap-3">
            <span className="text-2xl">📷</span>
            <p className="text-sm text-blue-800 font-medium">
              Recuerda escanear el código QR de la sala del AIP al llegar para registrar tu asistencia.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        <div className="lg:col-span-2">
          <Card className="bg-white/95 backdrop-blur-sm border-none shadow-2xl rounded-[2.5rem] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[#03361c] to-[#03361c] text-white py-8">
              <CardTitle className="text-3xl font-black text-center uppercase tracking-tighter">
                Reserva tu espacio
              </CardTitle>
            </CardHeader>
            <CardContent className="p-10">
              <div className="flex justify-center mb-10">
                <div className="p-2 bg-[#1a4d2e] rounded-[2.2rem] shadow-2xl">
                  <div className="bg-[#fdfbf7] p-6 rounded-[2rem]">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      locale={es}
                      // En el componente Calendar (línea 171 aprox.)
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        const isCurrentMonth = isSameMonth(date, currentDate);
                        const isNextMonth = isSameMonth(
                          date,
                          new Date(
                            currentDate.getFullYear(),
                            currentDate.getMonth() + 1,
                            1,
                          ),
                        );

                        // Bloquear si: es domingo OR es fecha pasada OR (no es mes actual AND no es el próximo mes habilitado)
                        return (
                          date.getDay() === 0 ||
                          date < today ||
                          (!isCurrentMonth &&
                            !(isNextMonth && isNextMonthEnabled))
                        );
                      }}
                      modifiersStyles={{
                        selected: {
                          backgroundColor: "#7e0505", 
                          color: "white",
                          fontWeight: "bold",
                          borderRadius: "12px",
                        },
                        today: {
                          border: "2px solid #f59e0b",
                          color: "#92400e",
                          fontWeight: "black",
                        },
                      }}
                      classNames={{
                        caption:
                          "flex justify-center pt-2 relative items-center text-[#7e0505] font-black text-xl mb-4 uppercase",
                        day: "h-12 w-12 p-0 font-bold hover:bg-[#1a4d2e]/10 rounded-xl transition-all flex items-center justify-center",
                      }}
                    />
                  </div>
                </div>
              </div>

              {selectedDate && (
                <div className="mt-10 text-center animate-in fade-in zoom-in duration-300">
                  <p className="text-2xl font-black text-[#1a4d2e] mb-6 border-b-2 border-[#1a4d2e]/10 pb-2 inline-block uppercase">
                    {format(selectedDate, 'EEEE dd MMMM', { locale: es })}
                  </p>
                  <div className="flex justify-center gap-6 mb-10">
                    {['mañana', 'tarde'].map(shift => (
                      <button
                        key={shift}
                        onClick={() => setSelectedShift(shift)}
                        className={`flex-1 max-w-[220px] py-6 rounded-2xl text-xl font-black transition-all flex flex-col items-center gap-2 shadow-lg ${
                          selectedShift === shift
                            ? shift.toLowerCase() === "mañana"
                              ? "bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] text-white ring-4 ring-blue-100" // Azul Hexadecimal exacto al seleccionar
                              : "bg-gradient-to-br from-orange-400 to-orange-600 text-white ring-4 ring-orange-100" // Tarde seleccionado
                            : "bg-white text-gray-600 border-2 border-gray-100 hover:border-blue-200" // No seleccionados
                        }`}
                      >
                        <span className="text-3xl">
                          {shift.toLowerCase() === "mañana" ? "☀️" : "⛅"}
                        </span>{" "}
                        {shift.toUpperCase()}
                      </button>
                    ))}
                  </div>
                    
                 {selectedShift && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
                      {(timeSlotsByShift[selectedShift] || []).map((slot, idx) => {
                        const status = getSlotStatus(slot);
                        const isMine = status.text.includes("ti");
                        const icons = [
                          "📖",
                          "💻",
                          "📝",
                          "🔬",
                          "🎨",
                          "📚",
                          "🧠",
                        ];
      
      return (
                          <button
                            key={slot}
                            disabled={status.disabled}
                            onClick={() => toggleTimeSlotSelection(slot)}
                            className={`relative p-5 rounded-2xl border-2 flex flex-col items-center justify-center min-h-[120px] transition-all shadow-sm ${
                              status.disabled
                                ? isMine
                                  ? "bg-green-100 border-green-400"
                                  : "bg-red-50 border-red-200 shadow-inner"
                                : selectedTimeSlots.includes(slot)
                                  ? "bg-[#2d6a4f] text-white shadow-xl scale-105 font-bold border-transparent"
                                  : "bg-white text-gray-700 border-gray-100 hover:border-[#03361c]"
                            }`}
                          >
                            {/* Nº DE HORA (1H, 2H...) + ICONO en la misma fila */}
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-3xl font-black leading-none ${status.disabled ? "text-gray-900" : ""}`}
                              >
                                {idx + 1}H
                              </span>
                              <span
                                className={`text-lg ${status.disabled && !isMine ? "opacity-60" : ""}`}
                              >
                                {icons[idx % icons.length]}
                              </span>
                            </div>

                            {/* HORARIO (Números grandes y bien negros para que resalten sobre el rojo suave) */}
                            <span
                              className={`text-sm sm:text-base font-bold mt-1 ${status.disabled ? "text-gray-900" : ""}`}
                            >
                              {slot}
                            </span>

                            {/* ETIQUETA DE ESTADO/NOMBRE (Rojo fuerte para otros, Verde para ti) */}
                            {status.text && (
                              <span
                                className={`text-[12px] font-black mt-2 px-2 py-1.5 rounded-lg uppercase tracking-tighter leading-tight text-center shadow-md ${
                                  isMine
                                    ? "bg-green-700 text-white"
                                    : "bg-red-600 text-white"
                                }`}
                              >
                                {status.text}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {selectedTimeSlots.length === 1 && !wouldExceedLimit && (
                    <p className="text-center text-sm text-gray-500 mt-6 max-w-2xl mx-auto">
                      💡 Tip: puedes tocar el bloque anterior o siguiente (incluso si hay recreo de por medio) para reservar 2 bloques seguidos.
                    </p>
                  )}

                  {selectedTimeSlots.length > 0 && (   //El boton Confirmar Reserva solo aparece cuando se ha seleccionado un turno, evitando confusiones y mejorando la experiencia de usuario
                    <Button   //Este codigo permite que el boton Confirmar Reserva, se ajuste en celulares. 
                      onClick={handleOpenReservationDetails}
                      disabled={wouldExceedLimit}
                      className={`
                        mt-12
                        w-full
                        max-w-2xl
                        mx-auto
                        py-6
                        px-3
                        font-black
                        rounded-2xl
                        shadow-2xl
                        transition-all
                        transform
                        uppercase
                        leading-tight
                        whitespace-normal
                        break-words
                        ${
                          wouldExceedLimit
                            ? "text-sm sm:text-lg md:text-xl tracking-normal bg-gray-400 cursor-not-allowed"
                            : "text-lg sm:text-xl md:text-2xl bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white hover:scale-105 hover:shadow-green-900/20"
                        }
                      `}
                    >
                      {wouldExceedLimit
                        ? `Límite alcanzado en turno ${selectedShift}`
                        : selectedTimeSlots.length === 2
                          ? "✅ Confirmar Reserva (2 bloques)"
                          : "✅ Confirmar Reserva"}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="bg-white/95 backdrop-blur-sm border-none shadow-2xl rounded-[2rem] h-full flex flex-col overflow-hidden">
            <CardHeader className="bg-[#03361c] text-white pb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <CardTitle className="text-2xl font-black uppercase tracking-tight">
                    Mis Reservas
                  </CardTitle>
                  <div className="flex gap-2 mt-2">
                    <span className="bg-white/10 px-2 py-1 rounded text-[10px] font-bold border border-white/30 uppercase">
                      Total: {currentAndFuture.length + past.length}
                    </span>
                    <span className="bg-yellow-400/0 text-yellow-200 px-2 py-1 rounded text-[10px] font-bold border border-yellow-400/40 uppercase">
                      Pendiente Sem:{" "}
                      {
                        currentAndFuture.filter((r) => {
                          const rd = toDateLocal(r.date);
                          const h = new Date();
                          const lun = new Date(
                            h.setDate(
                              h.getDate() -
                                (h.getDay() === 0 ? 6 : h.getDay() - 1),
                            ),
                          );
                          lun.setHours(0, 0, 0, 0);
                          const sab = new Date(lun);
                          sab.setDate(lun.getDate() + 5);
                          sab.setHours(23, 59, 59);
                          return rd >= lun && rd <= sab;
                        }).length
                      }
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={async () => {
                      setIsDownloadingWeek(true);

                      setTimeout(() => {
                        const doc = generateCurrentWeekPDF(myReservations, userData, timeSlotsByShift);

                        if (doc)
                          doc.save(`Semana_${format(new Date(), "dd-MM")}.pdf`);
                        else toast.info("No hay reservas esta semana");

                        setIsDownloadingWeek(false);
                      }, 800); // pequeña animación visible
                    }}
                    disabled={isDownloadingWeek}
                    className="
                      h-9
                      px-4
                      text-xs
                      font-bold
                      flex
                      items-center
                      gap-2
                      bg-gradient-to-r
                      from-[#40a779]
                      to-[#36b46d]
                      text-white
                      hover:scale-105
                      transition-all
                      duration-300
                      rounded-xl
                      shadow-md
                    "
                  >
                    {isDownloadingWeek ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Descargando...
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        DESCARGA SEMANA
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={openDownloadModal}
                    className="
                      h-9
                      px-4
                      text-xs
                      font-bold
                      flex
                      items-center
                      gap-2
                      bg-gradient-to-r
                      from-[#7e0505]
                      to-[#a30808]
                      text-white
                      hover:scale-105
                      transition-all
                      duration-300
                      rounded-xl
                      shadow-md
                    "
                  >
                    <FileText size={16} />
                    DESCARGAR INFORME
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowPastReservations(!showPastReservations)}
                className="
                  w-full
                  mt-3
                  h-9
                  text-xs
                  font-bold
                  flex
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-[#f5e6d3]
                  text-[#1a4d2e]
                  hover:bg-[#e8d8c3]
                  transition-all
                  duration-300
                  uppercase
                  shadow-sm
                "
              >
                <Eye size={16} />
                {showPastReservations ? "Ver Actuales" : "Ver Reservas Pasadas"}
              </Button>
            </CardHeader>
            <CardContent className="p-4 flex-grow">
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-2 custom-scrollbar">
                {(showPastReservations ? past : currentAndFuture)
                  .filter(r => r && r.date)
                  .sort((a, b) =>
                    showPastReservations
                      ? new Date(b.date) - new Date(a.date)
                      : new Date(a.date) - new Date(b.date)
                  )
                  .map((r) => {
                    const noAsistio = r.asistio === false;
                    const justCheckedIn = justCheckedInIds.includes(r.id);
                    // ✅ Solo mostramos "Asistió" si el backend confirmó asistio === true
                    // (por check-in QR o marca manual del admin) — antes se asumía
                    // "asistió" para cualquier reserva pasada sin marcar explícitamente.
                    const showAsistioBadge = !noAsistio && (justCheckedIn || r.asistio === true);
                    const pendiente = !noAsistio && !showAsistioBadge && isReservationPast(r);
                    const pendingTransfer = outgoingTransfers.find(
                      (t) => t.reservation_id === r.id && t.status === "pending"
                    );
                    return (
                    <div
                      key={r.id}
                      className={`group flex justify-between items-center p-4 rounded-xl border-2 transition-all ${
                        pendingTransfer
                          ? "bg-blue-50/50 border-blue-200"
                          : noAsistio
                          ? "bg-red-50/50 border-red-200"
                          : justCheckedIn
                          ? "bg-green-50/60 border-green-300"
                          : showPastReservations
                          ? "bg-gray-50/50 opacity-60 border-gray-100"
                          : "bg-white border-gray-50 hover:border-[#1a4d2e]/20 hover:shadow-md"
                      }`}
                    >
                      <div className={noAsistio ? "opacity-70" : ""}>
                        {noAsistio && (
                          <span className="inline-block bg-red-600 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded mb-1 tracking-wide">
                            No asistió
                          </span>
                        )}
                        {showAsistioBadge && (
                          <span className="inline-block bg-green-600 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded mb-1 tracking-wide">
                            ✓ {justCheckedIn ? "Check-in registrado" : "Asistió"}
                          </span>
                        )}
                        {pendiente && (
                          <span className="inline-block bg-amber-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded mb-1 tracking-wide">
                            ⏳ Pendiente de confirmar
                          </span>
                        )}
                        {pendingTransfer && (
                          <span className="inline-block bg-blue-600 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded mb-1 tracking-wide">
                            🔄 Cesión en proceso → {pendingTransfer.to_name || pendingTransfer.to_dni}
                          </span>
                        )}
                        <p className={`text-[10px] font-black text-[#990303] uppercase mb-1 ${noAsistio ? 'line-through' : ''}`}> 
                          {(() => {
                            const d = toDateLocal(r.date);
                            return d ? format(d, "EEEE dd MMMM", { locale: es }) : "";
                          })()}
                        </p>
                        <p className={`text-[15px] font-bold text-gray-800 tracking-tight ${noAsistio ? 'line-through' : ''}`}>
                          {getHourNumber(r.shift, r.time_slot) && (
                            <span className="text-[#03361c] font-black mr-1">
                              {getHourNumber(r.shift, r.time_slot)}H ·
                            </span>
                          )}
                          {r.time_slot}
                        </p>
                        <p className={`text-[9px] text-gray-400 font-bold uppercase tracking-widest ${noAsistio ? 'line-through' : ''}`}>
                          Turno {r.shift}
                        </p>
                        {(r.grado || r.seccion || r.area || r.actividad) && (
                          <p className={`text-[11px] text-[#1a4d2e] font-semibold mt-1 ${noAsistio ? 'line-through' : ''}`}>
                            {r.grado && `${r.grado}`}{r.seccion && ` "${r.seccion}"`}
                            {(r.grado || r.seccion) && (r.area || r.actividad) && ' · '}
                            {r.area}{r.area && r.actividad && ' · '}{r.actividad}
                          </p>
                        )}
                        {r.attachments && r.attachments.length > 0 && (
                          <p className="text-[10px] text-blue-600 font-bold mt-1 truncate">
                            📎 {r.attachments.length === 1 ? r.attachments[0].filename : `${r.attachments.length} archivos adjuntos`}
                          </p>
                        )}
                      </div>
                      {!showPastReservations && (
                        <div className="flex flex-col gap-2 items-end">
                          {pendingTransfer ? (
                            <Button
                              onClick={() => handleCancelTransfer(pendingTransfer.id)}
                              variant="outline"
                              size="sm"
                              className="text-[10px] border-blue-400 text-blue-700 hover:bg-blue-600 hover:text-white font-bold px-2 py-1 h-auto"
                            >
                              Cancelar cesión
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => handleOpenEditModal(r)}
                                variant="outline"
                                size="sm"
                                className="text-[10px] border-amber-500/60 text-amber-700 hover:bg-amber-500 hover:text-white font-bold px-2 py-1 h-auto opacity-0 group-hover:opacity-100 transition-all"
                              >
                                ✏️ Editar
                              </Button>
                              <Button
                                onClick={() => handleOpenTransferModal(r)}
                                variant="outline"
                                size="sm"
                                className="text-[10px] border-[#1a4d2e]/40 text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-bold px-2 py-1 h-auto opacity-0 group-hover:opacity-100 transition-all"
                              >
                                🔁 Ceder
                              </Button>
                              <Button
                                onClick={() => handleDeleteReservation(r.id)}
                                variant="destructive"
                                size="sm"
                                className="h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg shadow-red-200"
                              >
                                ✕
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ✅ Top 5 Docentes Activos de mi(s) propio(s) nivel(es) — controlado por el admin */}
      {(() => {
        if (!topTeachersVisible) return null;

        const userNiveles = parseNiveles(userData?.nivel);
        const esDirectivoOAip = userNiveles.some((n) =>
          ["directivo", "aip"].includes(n.trim().toLowerCase())
        );

        // Directivo y AIP ven siempre los 3 niveles reales, sin importar su nivel propio.
        // Los demás docentes solo ven el o los niveles en los que trabajan.
        const nivelesAMostrar = esDirectivoOAip
          ? ["Inicial", "Primaria", "Secundaria"]
          : userNiveles.filter((n) => ["Inicial", "Primaria", "Secundaria"].includes(n));

        if (nivelesAMostrar.length === 0) return null;

        // ✅ Descargar PDF: Top 5 Docentes Activos — 1 nivel para un docente
        // normal, o los 3 niveles combinados si es directivo/AIP.
        const handleDownloadTopTeachersPDF = () => {
          const hayDatos = nivelesAMostrar.some((n) => (topTeachersByLevel[n] || []).length > 0);
          if (!hayDatos) {
            toast.info('Todavía no hay datos suficientes del Top 5 para exportar.');
            return;
          }
          const monthLabel = format(new Date(), "MMMM yyyy", { locale: es });
          const pdf = new jsPDF('p', 'mm', 'a4');
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const marginX = 15;
          const usableWidth = pageWidth - marginX * 2;
          const rowHeight = 8;
          const colWidths = nivelesAMostrar.length > 1 ? [45, 25, 80, 30] : [25, 90, 30];
          const headers = nivelesAMostrar.length > 1
            ? ['Nivel', 'Puesto', 'Docente', 'Reservas']
            : ['Puesto', 'Docente', 'Reservas'];
          let y;

          const drawHeader = () => {
            pdf.setFillColor(122, 79, 26); // #7a4f1a
            pdf.rect(0, 0, pageWidth, 28, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(15);
            pdf.text('Top 5 Docentes Activos', marginX, 14);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.text(`Mes: ${monthLabel} — ${nivelesAMostrar.join(', ')}`, marginX, 21);
            pdf.setFontSize(8);
            pdf.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth - marginX, 21, { align: 'right' });
          };

          const drawTableHeader = () => {
            pdf.setFillColor(122, 79, 26);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.rect(marginX, y, usableWidth, rowHeight, 'F');
            let x = marginX;
            headers.forEach((h, i) => {
              pdf.text(h, x + 2, y + rowHeight - 2.5);
              x += colWidths[i];
            });
            y += rowHeight;
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
          };

          drawHeader();
          y = 38;
          drawTableHeader();

          const rows = [];
          nivelesAMostrar.forEach((nivel) => {
            (topTeachersByLevel[nivel] || []).forEach((t, idx) => {
              rows.push(
                nivelesAMostrar.length > 1
                  ? [nivel, `${idx + 1}º`, t.name, String(t.count)]
                  : [`${idx + 1}º`, t.name, String(t.count)]
              );
            });
          });

          rows.forEach((row, idx) => {
            if (y + rowHeight > pageHeight - 15) {
              pdf.addPage();
              y = 20;
              drawTableHeader();
            }
            if (idx % 2 === 1) {
              pdf.setFillColor(245, 230, 211); // #f5e6d3
              pdf.rect(marginX, y, usableWidth, rowHeight, 'F');
            }
            let x = marginX;
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            row.forEach((cell, i) => {
              pdf.text(String(cell ?? ''), x + 2, y + rowHeight - 2.5);
              x += colWidths[i];
            });
            y += rowHeight;
          });

          pdf.save(`Top5_Docentes_${format(new Date(), 'yyyy-MM')}.pdf`);
        };

        return (
          <div className="max-w-7xl mx-auto mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-black text-white uppercase tracking-tight">
                🏆 Top 5 Docentes Activos
              </h3>
              <Button
                onClick={handleDownloadTopTeachersPDF}
                size="sm"
                className="bg-[#7a4f1a] hover:bg-[#5f3d14] text-white font-bold"
              >
                📄 Descargar PDF
              </Button>
            </div>
            <div className={`grid grid-cols-1 ${nivelesAMostrar.length > 1 ? "md:grid-cols-2" : ""} gap-6`}>
              {nivelesAMostrar.map((nivel) => {
                const top = topTeachersByLevel[nivel] || [];
                let headerGradient = "from-[#7a4f1a] to-[#9a6a2a]";
                let barColor = "bg-[#7a4f1a]";
                let emoji = "🏆";
                if (nivel === 'Inicial') { headerGradient = "from-pink-500 to-pink-600"; barColor = "bg-pink-500"; emoji = "🎈"; }
                if (nivel === 'Primaria') { headerGradient = "from-yellow-500 to-amber-600"; barColor = "bg-amber-500"; emoji = "✏️"; }
                if (nivel === 'Secundaria') { headerGradient = "from-blue-600 to-blue-700"; barColor = "bg-blue-600"; emoji = "🎓"; }
                return (
                  <Card key={nivel} className="bg-white/95 backdrop-blur-sm border-none shadow-xl rounded-[2rem] overflow-hidden">
                    <CardHeader className={`bg-gradient-to-r ${headerGradient} text-white py-5`}>
                      <CardTitle className="text-lg font-black text-center uppercase tracking-tight">
                        {emoji} Top 5 Docentes Activos — {nivel}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {top.length === 0 ? (
                        <p className="text-center text-gray-400 py-6 text-sm">
                          Todavía no hay datos suficientes este mes.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {top.map((t, index) => (
                            <div key={index} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors">
                              <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0
                                ${index === 0 ? 'bg-yellow-500' :
                                  index === 1 ? 'bg-gray-400' :
                                  index === 2 ? 'bg-orange-400' : 'bg-[#1a4d2e]/80'}
                              `}>
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-gray-800 text-sm truncate">{t.name}</p>
                                <div className="w-full bg-gray-200 h-1 rounded-full mt-1">
                                  <div
                                    className={`${barColor} h-1 rounded-full`}
                                    style={{ width: `${(t.count / top[0].count) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                              <span className={`text-lg font-black shrink-0 ${
                                nivel === 'Inicial' ? 'text-pink-600' :
                                nivel === 'Primaria' ? 'text-amber-600' :
                                nivel === 'Secundaria' ? 'text-blue-700' : 'text-[#7a4f1a]'
                              }`}>{t.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}

      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6 border-b-2 border-gray-50 pb-4">
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                {needsLevelPick ? "Nivel, Año y Meses" : "Seleccionar Año y Meses"}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="text-[#1a4d2e] font-black underline hover:bg-red-50"
                onClick={() => {
                  if (selectedMonths.size === monthsWithData.length)
                    setSelectedMonths(new Set());
                  else setSelectedMonths(new Set(monthsWithData));
                }}
              >
                {selectedMonths.size === monthsWithData.length
                  ? "Deseleccionar"
                  : "Todos"}
              </Button>
            </div>

            {/* 🗓️ Selector de año: los meses de abajo son siempre del año
                elegido aquí, para no mezclar años en un mismo informe. */}
            {needsLevelPick && (
              <div className="mb-4">
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Nivel
                </label>
                <select
                  value={selectedReportLevel}
                  onChange={(e) => handleSelectReportLevel(e.target.value)}
                  className="w-full border-2 border-gray-100 rounded-xl p-3 text-gray-800 font-bold focus:outline-none focus:ring-2 focus:ring-[#1a4d2e]"
                >
                  {reportLevels.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                Año
              </label>
              <select
                value={selectedReportYear}
                onChange={(e) => handleSelectReportYear(e.target.value)}
                className="w-full border-2 border-gray-100 rounded-xl p-3 text-gray-800 font-bold focus:outline-none focus:ring-2 focus:ring-[#1a4d2e]"
              >
                {availableReportYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8 max-h-56 overflow-y-auto pr-2 custom-scrollbar">
              {monthsWithData.length === 0 && (
                <p className="col-span-2 text-center text-gray-400 text-sm py-4 italic">
                  No tienes reservas en {selectedReportYear}.
                </p>
              )}
              {monthsWithData.map((m) => (
                <label
                  key={m}
                  className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${selectedMonths.has(m) ? "bg-green-50 border-[#1a4d2e] shadow-sm" : "border-gray-50 hover:border-gray-200"}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMonths.has(m)}
                    onChange={() => {
                      const n = new Set(selectedMonths);
                      n.has(m) ? n.delete(m) : n.add(m);
                      setSelectedMonths(n);
                    }}
                    className="h-4 w-4 text-[#03361c] rounded"
                  />
                  <span className="text-sm font-black text-gray-700 uppercase">
                    {getMonthName(m)}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                disabled={selectedMonths.size === 0}
                className="flex-1 bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black py-7 rounded-xl shadow-xl shadow-green-900/20"
                onClick={() => {
                  const filtered = myReservations.filter((r) => {
                    const d = toDateLocal(r.date);
                    if (!d) return false;
                    if (d.getFullYear() !== Number(selectedReportYear)) return false;
                    if (!matchesReportLevel(r, selectedReportLevel)) return false;
                    return selectedMonths.has(d.getMonth() + 1);
                  });
                  generateReservationsPDF(
                    filtered,
                    userData,
                    Array.from(selectedMonths),
                    selectedReportYear,
                    needsLevelPick ? selectedReportLevel : null,
                  );
                  setIsDownloadModalOpen(false);
                }}
              >
                DESCARGAR ({selectedMonths.size})
              </Button>
              <Button
                onClick={() => setIsDownloadModalOpen(false)}
                variant="outline"
                className="flex-1 bg-gradient-to-r from-[#f11f03] to-[#ee8982] border-gray-900 text-gray-900 font-black py-7 rounded-xl"
              >
                CERRAR
              </Button>
            </div>
          </div>
        </div>
      )}
      {isReservationDetailsOpen && (() => {
        const userNiveles = parseNiveles(userData?.nivel);
        const requiereSeleccionNivel = userNiveles.length > 1;
        const nivelActivo = reservationDetails.nivel || (userNiveles.length === 1 ? userNiveles[0] : "");
        const config = getNivelFormConfig(nivelActivo);
        const camposListos = !requiereSeleccionNivel || !!reservationDetails.nivel;

        // 🔁 Rango permitido para "repetir hasta": mismo mes actual, o
        // también el próximo mes si ya está habilitado (últimos 2 días).
        const endOfNextMonthForRepeat = endOfMonth(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        const maxRepeatUntilDate = isNextMonthEnabled ? endOfNextMonthForRepeat : endOfCurrMonth;

        const handleSelectNivel = (n) => {
          setReservationDetails((prev) => ({
            ...prev,
            nivel: n,
            grado: "",
            seccion: "",
            area: "",
          }));
        };

        return (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="flex justify-between items-center px-8 pt-8 pb-4 border-b-2 border-gray-50 shrink-0">
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                Detalles de la Reserva
              </h3>
            </div>

            <div className="space-y-4 px-8 py-4 overflow-y-auto">
              {/* NIVEL: solo se muestra si el docente trabaja en más de un nivel */}
              {requiereSeleccionNivel && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    ¿Para qué nivel es esta reserva?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {userNiveles.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handleSelectNivel(n)}
                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-colors ${
                          reservationDetails.nivel === n
                            ? "bg-[#1a4d2e] text-white border-[#1a4d2e]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* El resto de los campos solo aparece una vez elegido el nivel (si aplica) */}
              {camposListos && (
              <>
              {/* GRADO: lista según nivel, texto libre solo en modo genérico (freeform) */}
              {config.showGrado && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Grado/aula
                  </label>
                  {config.gradoOptions ? (
                    <select
                      value={reservationDetails.grado}
                      onChange={(e) =>
                        setReservationDetails((prev) => ({ ...prev, grado: e.target.value }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el grado/aula</option>
                      {config.gradoOptions.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={reservationDetails.grado}
                      onChange={(e) =>
                        setReservationDetails((prev) => ({ ...prev, grado: e.target.value }))
                      }
                      placeholder="Ej: 3°"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              {/* SECCIÓN: no se muestra para Inicial ni para Directivo/AIP */}
              {config.showSeccion && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Sección
                  </label>
                  <input
                    type="text"
                    value={reservationDetails.seccion}
                    onChange={(e) =>
                      setReservationDetails((prev) => ({ ...prev, seccion: e.target.value.toUpperCase() }))
                    }
                    placeholder="Ej: A"
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none uppercase"
                  />
                </div>
              )}

              {/* ÁREA: lista fija según nivel, oculta para Directivo/AIP */}
              {config.showArea && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Área
                  </label>
                  {config.areaOptions ? (
                    <select
                      value={reservationDetails.area}
                      onChange={(e) =>
                        setReservationDetails((prev) => ({ ...prev, area: e.target.value }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el área</option>
                      {config.areaOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={reservationDetails.area}
                      onChange={(e) =>
                        setReservationDetails((prev) => ({ ...prev, area: e.target.value }))
                      }
                      placeholder="Ej: Comunicación"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              {/* ACTIVIDAD: siempre visible, para todos los niveles */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Actividad
                </label>
                <input
                  type="text"
                  value={reservationDetails.actividad}
                  onChange={(e) =>
                    setReservationDetails((prev) => ({ ...prev, actividad: e.target.value }))
                  }
                  placeholder="Ej: Elaboración de trípticos"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>

              {/* 📎 ARCHIVOS ADJUNTOS: opcional, se pueden elegir varios. Se
                  guardan solo hasta que el admin los descargue (almacenamiento
                  temporal, no permanente). */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Adjuntar archivo(s) <span className="normal-case font-semibold text-gray-400">(opcional)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Word, Excel, PowerPoint, PDF, HTML, JSON, imágenes, audio o video — máx. {ATTACHMENT_MAX_MB} MB c/u, hasta {ATTACHMENT_MAX_COUNT} archivos. Se guardan temporalmente hasta que el administrador los descargue.
                </p>
                {attachmentFiles.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {attachmentFiles.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        className="flex items-center justify-between gap-3 border-2 border-[#1a4d2e]/20 bg-[#1a4d2e]/5 rounded-xl px-4 py-3"
                      >
                        <span className="text-sm font-bold text-gray-700 truncate">📎 {f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachmentFile(idx)}
                          className="text-red-500 hover:text-red-700 font-black text-sm shrink-0"
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {attachmentFiles.length < ATTACHMENT_MAX_COUNT && (
                  <input
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    multiple
                    onChange={(e) => handleAttachmentChange(e, 0)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#1a4d2e] file:text-white file:font-bold"
                  />
                )}
              </div>

              {/* RECURSOS DIGITALES / TECNOLÓGICOS: siempre visible, para todos los niveles */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Recursos Digitales / Tecnológicos
                </label>
                <input
                  type="text"
                  value={reservationDetails.recursos_digitales}
                  onChange={(e) =>
                    setReservationDetails((prev) => ({ ...prev, recursos_digitales: e.target.value }))
                  }
                  placeholder="Ej: Internet, Plataformas educativas, laboratorios virtuales, entre otros."
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>

              {/* ENLACES: opcional, siempre visible, para todos los niveles.
                  El docente puede agregar cuantos enlaces necesite con el botón "+". */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Enlaces <span className="normal-case font-semibold text-gray-400">(opcional)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Agrega los enlaces de las páginas que vas a trabajar en la sesión.
                </p>
                <div className="space-y-2">
                  {reservationDetails.enlaces.map((enlace, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="url"
                        value={enlace}
                        onChange={(e) =>
                          setReservationDetails((prev) => {
                            const nuevos = [...prev.enlaces];
                            nuevos[idx] = e.target.value;
                            return { ...prev, enlaces: nuevos };
                          })
                        }
                        placeholder="https://..."
                        className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setReservationDetails((prev) => ({
                            ...prev,
                            enlaces: prev.enlaces.filter((_, i) => i !== idx),
                          }))
                        }
                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 font-black text-lg"
                        title="Quitar enlace"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setReservationDetails((prev) => ({ ...prev, enlaces: [...prev.enlaces, ""] }))
                  }
                  className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 font-bold text-sm hover:border-[#1a4d2e] hover:text-[#1a4d2e] transition-colors"
                >
                  <span className="text-lg leading-none">+</span> Agregar enlace
                </button>
              </div>

              {/* RECURSOS MATERIALES: selección múltiple, siempre visible, para todos los niveles */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Recursos Materiales
                </label>
                <div className="flex flex-wrap gap-2">
                  {RECURSOS_MATERIALES_OPTIONS.map((recurso) => {
                    const isSelected = reservationDetails.recursos_materiales.includes(recurso);
                    return (
                      <button
                        key={recurso}
                        type="button"
                        onClick={() =>
                          setReservationDetails((prev) => ({
                            ...prev,
                            recursos_materiales: isSelected
                              ? prev.recursos_materiales.filter((r) => r !== recurso)
                              : [...prev.recursos_materiales, recurso],
                          }))
                        }
                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-colors ${
                          isSelected
                            ? "bg-[#1a4d2e] text-white border-[#1a4d2e]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50"
                        }`}
                      >
                        {recurso}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* COMPETENCIA: siempre visible, para todos los niveles */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Competencia
                </label>
                <input
                  type="text"
                  value={reservationDetails.competencia}
                  onChange={(e) =>
                    setReservationDetails((prev) => ({ ...prev, competencia: e.target.value }))
                  }
                  placeholder="Ej: Competencia transversal y/o competencias del área."
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>
              </>
              )}

              {/* 🔁 REPETIR SEMANALMENTE (opcional) */}
              {camposListos && selectedDate && (
                <div className="border-t border-gray-100 pt-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={repeatWeekly}
                      onChange={(e) => {
                        setRepeatWeekly(e.target.checked);
                        if (!e.target.checked) setRepeatUntil("");
                      }}
                      className="w-5 h-5 accent-[#1a4d2e]"
                    />
                    <span className="text-sm font-bold text-gray-700">
                      🔁 Repetir esta reserva cada semana (mismo día y horario)
                    </span>
                  </label>
                  {repeatWeekly && (
                    <div className="mt-3">
                      <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                        Repetir hasta
                      </label>
                      <input
                        type="date"
                        value={repeatUntil}
                        min={format(selectedDate, "yyyy-MM-dd")}
                        max={format(maxRepeatUntilDate, "yyyy-MM-dd")}
                        onChange={(e) => setRepeatUntil(e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Se creará una reserva todos los {format(selectedDate, "EEEE", { locale: es })} hasta esa fecha.
                        Si algún día ya está ocupado, se omite automáticamente y se avisa cuáles.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-4 px-8 pb-8 pt-4 border-t-2 border-gray-50 shrink-0">
              <Button
                className="flex-1 bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black py-6 rounded-xl shadow-xl shadow-green-900/20"
                onClick={handleConfirmReservationDetails}
                disabled={isSubmittingRecurring || isUploadingAttachment}
              >
                {isUploadingAttachment ? "SUBIENDO ARCHIVO..." : isSubmittingRecurring ? "CREANDO..." : "CONFIRMAR RESERVA"}
              </Button>
              <Button
                onClick={() => {
                  setIsReservationDetailsOpen(false);
                  setAttachmentFiles([]);
                }}
                variant="outline"
                className="flex-1 bg-gradient-to-r from-[#f11f03] to-[#ee8982] border-gray-900 text-gray-900 font-black py-6 rounded-xl"
              >
                CANCELAR
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ✅ Modal: Ceder reserva (Docente A ingresa el DNI del docente destino) */}
      {isTransferModalOpen && transferReservation && (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-4 border-b-2 border-gray-50 pb-4">
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                Ceder Reserva
              </h3>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm">
              <p className="font-bold text-gray-700">
                {(() => {
                  const d = toDateLocal(transferReservation.date);
                  return d ? format(d, "EEEE dd MMMM", { locale: es }) : "";
                })()}
              </p>
              <p className="text-gray-600">
                {getHourNumber(transferReservation.shift, transferReservation.time_slot) && (
                  <span className="font-black">{getHourNumber(transferReservation.shift, transferReservation.time_slot)}H · </span>
                )}
                {transferReservation.time_slot} · Turno {transferReservation.shift}
              </p>
            </div>

            <p className="text-xs text-gray-500 mb-2">
              Ingresa el DNI del docente al que le cederás esta reserva. El horario no cambiará;
              el docente deberá aceptar antes de que el cambio sea definitivo.
            </p>
            <label className="block text-xs font-black text-gray-500 uppercase mb-1">
              DNI del docente
            </label>
            <input
              type="text"
              value={transferToDni}
              onChange={(e) => setTransferToDni(e.target.value.replace(/\D/g, ""))}
              placeholder="Ej: 12345678"
              maxLength={15}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none mb-6"
            />

            <div className="flex gap-4">
              <Button
                className="flex-1 bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black py-6 rounded-xl shadow-xl shadow-green-900/20 disabled:opacity-60"
                onClick={handleSubmitTransfer}
                disabled={isSubmittingTransfer}
              >
                {isSubmittingTransfer ? "Enviando..." : "ENVIAR SOLICITUD"}
              </Button>
              <Button
                onClick={() => {
                  setIsTransferModalOpen(false);
                  setTransferReservation(null);
                }}
                variant="outline"
                className="flex-1 bg-gradient-to-r from-[#f11f03] to-[#ee8982] border-gray-900 text-gray-900 font-black py-6 rounded-xl"
              >
                CANCELAR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Banner: cesiones pendientes de aceptar (Docente B) */}
      {incomingTransfers.length > 0 && (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg p-8 animate-in zoom-in duration-200 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-6 border-b-2 border-gray-50 pb-4">
              <span className="text-3xl">🔁</span>
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                Reservas por Aceptar
              </h3>
            </div>

            <div className="space-y-4">
              {incomingTransfers.map((t) => (
                <div key={t.id} className="border-2 border-blue-100 bg-blue-50/50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-bold text-[#1a4d2e]">{t.from_name}</span> te quiere ceder su reserva:
                  </p>
                  <p className="font-bold text-gray-800">
                    {(() => {
                      const d = toDateLocal(t.reservation?.date);
                      return d ? format(d, "EEEE dd MMMM", { locale: es }) : "";
                    })()}
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    {getHourNumber(t.reservation?.shift, t.reservation?.time_slot) && (
                      <span className="font-black">{getHourNumber(t.reservation?.shift, t.reservation?.time_slot)}H · </span>
                    )}
                    {t.reservation?.time_slot} · Turno {t.reservation?.shift}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      size="sm"
                      className="flex-1 bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white font-bold"
                      onClick={() => handleOpenAcceptModal(t)}
                    >
                      ✓ Aceptar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-400 text-red-600 hover:bg-red-500 hover:text-white font-bold"
                      onClick={() => handleRejectTransfer(t.id)}
                    >
                      ✕ Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modal: Aceptar cesión — completa sus propios datos (el horario NO se puede modificar) */}
      {transferToAccept && (() => {
        const userNiveles = parseNiveles(userData?.nivel);
        const requiereSeleccionNivel = userNiveles.length > 1;
        const nivelActivo = acceptFormData.nivel || (userNiveles.length === 1 ? userNiveles[0] : "");
        const config = getNivelFormConfig(nivelActivo);
        const camposListos = !requiereSeleccionNivel || !!acceptFormData.nivel;

        const handleSelectNivel = (n) => {
          setAcceptFormData((prev) => ({
            ...prev,
            nivel: n,
            grado: "",
            seccion: "",
            area: "",
          }));
        };

        return (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-4 border-b-2 border-gray-50 pb-4">
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                Aceptar Reserva
              </h3>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm">
              <p className="font-bold text-gray-700">
                {(() => {
                  const d = toDateLocal(transferToAccept.reservation?.date);
                  return d ? format(d, "EEEE dd MMMM", { locale: es }) : "";
                })()}
              </p>
              <p className="text-gray-600">
                {getHourNumber(transferToAccept.reservation?.shift, transferToAccept.reservation?.time_slot) && (
                  <span className="font-black">{getHourNumber(transferToAccept.reservation?.shift, transferToAccept.reservation?.time_slot)}H · </span>
                )}
                {transferToAccept.reservation?.time_slot} · Turno {transferToAccept.reservation?.shift}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">El horario no se puede modificar.</p>
            </div>

            <div className="space-y-4 mb-8">
              {/* NIVEL: solo se muestra si el docente trabaja en más de un nivel */}
              {requiereSeleccionNivel && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    ¿Para qué nivel es esta reserva?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {userNiveles.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handleSelectNivel(n)}
                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-colors ${
                          acceptFormData.nivel === n
                            ? "bg-[#1a4d2e] text-white border-[#1a4d2e]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {camposListos && (
              <>
              {config.showGrado && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Grado
                  </label>
                  {config.gradoOptions ? (
                    <select
                      value={acceptFormData.grado}
                      onChange={(e) => setAcceptFormData((prev) => ({ ...prev, grado: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el grado</option>
                      {config.gradoOptions.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={acceptFormData.grado}
                      onChange={(e) => setAcceptFormData((prev) => ({ ...prev, grado: e.target.value }))}
                      placeholder="Ej: 3°"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              {config.showSeccion && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Sección
                  </label>
                  <input
                    type="text"
                    value={acceptFormData.seccion}
                    onChange={(e) => setAcceptFormData((prev) => ({ ...prev, seccion: e.target.value.toUpperCase() }))}
                    placeholder="Ej: A"
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none uppercase"
                  />
                </div>
              )}

              {config.showArea && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Área
                  </label>
                  {config.areaOptions ? (
                    <select
                      value={acceptFormData.area}
                      onChange={(e) => setAcceptFormData((prev) => ({ ...prev, area: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el área</option>
                      {config.areaOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={acceptFormData.area}
                      onChange={(e) => setAcceptFormData((prev) => ({ ...prev, area: e.target.value }))}
                      placeholder="Ej: Comunicación"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Actividad
                </label>
                <input
                  type="text"
                  value={acceptFormData.actividad}
                  onChange={(e) => setAcceptFormData((prev) => ({ ...prev, actividad: e.target.value }))}
                  placeholder="Ej: Elaboración de trípticos"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>
              </>
              )}
            </div>

            <div className="flex gap-4">
              <Button
                className="flex-1 bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black py-6 rounded-xl shadow-xl shadow-green-900/20 disabled:opacity-60"
                onClick={handleConfirmAccept}
                disabled={isSubmittingAccept}
              >
                {isSubmittingAccept ? "Guardando..." : "CONFIRMAR"}
              </Button>
              <Button
                onClick={() => setTransferToAccept(null)}
                variant="outline"
                className="flex-1 bg-gradient-to-r from-[#f11f03] to-[#ee8982] border-gray-900 text-gray-900 font-black py-6 rounded-xl"
              >
                CANCELAR
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ✅ Modal: Editar mi reserva — grado/sección/área/actividad (el horario NO se puede modificar) */}
      {editingReservation && (() => {
        const userNiveles = parseNiveles(userData?.nivel);
        const requiereSeleccionNivel = userNiveles.length > 1;
        const nivelActivo = editFormData.nivel || (userNiveles.length === 1 ? userNiveles[0] : "");
        const config = getNivelFormConfig(nivelActivo);
        const camposListos = !requiereSeleccionNivel || !!editFormData.nivel;

        const handleSelectNivel = (n) => {
          setEditFormData((prev) => ({
            ...prev,
            nivel: n,
            grado: "",
            seccion: "",
            area: "",
          }));
        };

        return (
        <div className="fixed inset-0 bg-[#0f2419]/80 backdrop-blur-md flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="flex justify-between items-center px-8 pt-8 pb-4 border-b-2 border-gray-50 shrink-0">
              <h3 className="text-2xl font-black text-[#03361c] tracking-tighter uppercase">
                Editar Reserva
              </h3>
            </div>

            <div className="space-y-4 px-8 py-4 overflow-y-auto">
            <div className="bg-gray-50 rounded-xl p-4 text-sm">
              <p className="font-bold text-gray-700">
                {(() => {
                  const d = toDateLocal(editingReservation.date);
                  return d ? format(d, "EEEE dd MMMM", { locale: es }) : "";
                })()}
              </p>
              <p className="text-gray-600">
                {getHourNumber(editingReservation.shift, editingReservation.time_slot) && (
                  <span className="font-black">{getHourNumber(editingReservation.shift, editingReservation.time_slot)}H · </span>
                )}
                {editingReservation.time_slot} · Turno {editingReservation.shift}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">El horario no se puede modificar.</p>
            </div>

            <div className="space-y-4">
              {requiereSeleccionNivel && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    ¿Para qué nivel es esta reserva?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {userNiveles.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handleSelectNivel(n)}
                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-colors ${
                          editFormData.nivel === n
                            ? "bg-[#1a4d2e] text-white border-[#1a4d2e]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {camposListos && (
              <>
              {config.showGrado && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Grado
                  </label>
                  {config.gradoOptions ? (
                    <select
                      value={editFormData.grado}
                      onChange={(e) => setEditFormData((prev) => ({ ...prev, grado: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el grado</option>
                      {config.gradoOptions.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={editFormData.grado}
                      onChange={(e) => setEditFormData((prev) => ({ ...prev, grado: e.target.value }))}
                      placeholder="Ej: 3°"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              {config.showSeccion && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Sección
                  </label>
                  <input
                    type="text"
                    value={editFormData.seccion}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, seccion: e.target.value.toUpperCase() }))}
                    placeholder="Ej: A"
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none uppercase"
                  />
                </div>
              )}

              {config.showArea && (
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                    Área
                  </label>
                  {config.areaOptions ? (
                    <select
                      value={editFormData.area}
                      onChange={(e) => setEditFormData((prev) => ({ ...prev, area: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none bg-white"
                    >
                      <option value="">Selecciona el área</option>
                      {config.areaOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={editFormData.area}
                      onChange={(e) => setEditFormData((prev) => ({ ...prev, area: e.target.value }))}
                      placeholder="Ej: Comunicación"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                    />
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Actividad
                </label>
                <input
                  type="text"
                  value={editFormData.actividad}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, actividad: e.target.value }))}
                  placeholder="Ej: Elaboración de trípticos"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>

              {/* 📎 ARCHIVOS ADJUNTOS: opcional, se pueden elegir varios.
                  Los ya subidos se pueden eliminar individualmente; los
                  nuevos se agregan a los que ya tiene la reserva. */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Adjuntar archivo(s) <span className="normal-case font-semibold text-gray-400">(opcional)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Word, Excel, PowerPoint, PDF, HTML, JSON, imágenes, audio o video — máx. {ATTACHMENT_MAX_MB} MB c/u, hasta {ATTACHMENT_MAX_COUNT} archivos en total. Se guardan temporalmente hasta que el administrador los descargue.
                </p>

                {(editingReservation.attachments || []).length > 0 && (
                  <div className="space-y-2 mb-2">
                    {editingReservation.attachments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between gap-3 border-2 border-gray-200 bg-gray-50 rounded-xl px-4 py-3"
                      >
                        <span className="text-sm font-bold text-gray-600 truncate">📎 {a.filename}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteExistingAttachment(editingReservation.id, a.id)}
                          className="text-red-500 hover:text-red-700 font-black text-sm shrink-0"
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {attachmentFiles.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {attachmentFiles.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        className="flex items-center justify-between gap-3 border-2 border-[#1a4d2e]/20 bg-[#1a4d2e]/5 rounded-xl px-4 py-3"
                      >
                        <span className="text-sm font-bold text-gray-700 truncate">📎 {f.name} (nuevo)</span>
                        <button
                          type="button"
                          onClick={() => removeAttachmentFile(idx)}
                          className="text-red-500 hover:text-red-700 font-black text-sm shrink-0"
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(editingReservation.attachments || []).length + attachmentFiles.length < ATTACHMENT_MAX_COUNT && (
                  <input
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    multiple
                    onChange={(e) => handleAttachmentChange(e, (editingReservation.attachments || []).length)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#1a4d2e] file:text-white file:font-bold"
                  />
                )}
              </div>

              {/* RECURSOS DIGITALES / TECNOLÓGICOS */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Recursos Digitales / Tecnológicos
                </label>
                <input
                  type="text"
                  value={editFormData.recursos_digitales}
                  onChange={(e) =>
                    setEditFormData((prev) => ({ ...prev, recursos_digitales: e.target.value }))
                  }
                  placeholder="Ej: Internet, Plataformas educativas, laboratorios virtuales, entre otros."
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>

              {/* ENLACES: opcional */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Enlaces <span className="normal-case font-semibold text-gray-400">(opcional)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Agrega los enlaces de las páginas que vas a trabajar en la sesión.
                </p>
                <div className="space-y-2">
                  {editFormData.enlaces.map((enlace, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="url"
                        value={enlace}
                        onChange={(e) =>
                          setEditFormData((prev) => {
                            const nuevos = [...prev.enlaces];
                            nuevos[idx] = e.target.value;
                            return { ...prev, enlaces: nuevos };
                          })
                        }
                        placeholder="https://..."
                        className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setEditFormData((prev) => ({
                            ...prev,
                            enlaces: prev.enlaces.filter((_, i) => i !== idx),
                          }))
                        }
                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 font-black text-lg"
                        title="Quitar enlace"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setEditFormData((prev) => ({ ...prev, enlaces: [...prev.enlaces, ""] }))
                  }
                  className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 font-bold text-sm hover:border-[#1a4d2e] hover:text-[#1a4d2e] transition-colors"
                >
                  <span className="text-lg leading-none">+</span> Agregar enlace
                </button>
              </div>

              {/* RECURSOS MATERIALES: selección múltiple */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Recursos Materiales
                </label>
                <div className="flex flex-wrap gap-2">
                  {RECURSOS_MATERIALES_OPTIONS.map((recurso) => {
                    const isSelected = editFormData.recursos_materiales.includes(recurso);
                    return (
                      <button
                        key={recurso}
                        type="button"
                        onClick={() =>
                          setEditFormData((prev) => ({
                            ...prev,
                            recursos_materiales: isSelected
                              ? prev.recursos_materiales.filter((r) => r !== recurso)
                              : [...prev.recursos_materiales, recurso],
                          }))
                        }
                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-colors ${
                          isSelected
                            ? "bg-[#1a4d2e] text-white border-[#1a4d2e]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50"
                        }`}
                      >
                        {recurso}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* COMPETENCIA */}
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase mb-1">
                  Competencia
                </label>
                <input
                  type="text"
                  value={editFormData.competencia}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, competencia: e.target.value }))}
                  placeholder="Ej: Competencia transversal y/o competencias del área."
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-bold text-gray-700 focus:border-[#1a4d2e] outline-none"
                />
              </div>
              </>
              )}
            </div>
            </div>

            <div className="flex gap-4 px-8 pb-8 pt-4 border-t-2 border-gray-50 shrink-0">
              <Button
                className="flex-1 bg-gradient-to-r from-[#03361c] to-[#2d6a4f] text-white font-black py-6 rounded-xl shadow-xl shadow-green-900/20 disabled:opacity-60"
                onClick={handleConfirmEdit}
                disabled={isSubmittingEdit || isUploadingAttachment}
              >
                {isUploadingAttachment ? "SUBIENDO ARCHIVO..." : isSubmittingEdit ? "Guardando..." : "GUARDAR CAMBIOS"}
              </Button>
              <Button
                onClick={() => {
                  setEditingReservation(null);
                  setAttachmentFiles([]);
                }}
                variant="outline"
                className="flex-1 bg-gradient-to-r from-[#f11f03] to-[#ee8982] border-gray-900 text-gray-900 font-black py-6 rounded-xl"
              >
                CANCELAR
              </Button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};
export default UserDashboard;